import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Building2, 
  Server,
  Shield,
  CheckCircle2,
  Sparkles,
  Target
} from 'lucide-react';
import GenerationProgress from './GenerationProgress';
import DiagnosticDocumentViewer from './DiagnosticDocumentViewer';
import { chatCompletions } from '../services/openaiBackend';

interface DiagnosticTIWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GeneratedDocuments {
  bilanSante: string; // Bilan de santé TI (COBIT/ITIL)
  auditSecurite: string; // Audit de sécurité (NIST/ISO)
}

const QUESTIONS = {
  phase1: {
    title: "Profil Organisationnel",
    icon: Building2,
    description: "Informations générales sur l'organisation",
    questions: [
      {
        id: "company_name",
        question: "Nom de l'organisation",
        type: "text",
        required: true
      },
      {
        id: "industry",
        question: "Secteur d'activité",
        type: "select",
        options: [
          "Cabinet d'avocats",
          "Cabinet comptable / CPA",
          "Services professionnels",
          "Manufacturing",
          "Commerce de détail",
          "Santé",
          "Finance / Assurance",
          "Technologies",
          "Construction",
          "Transport / Logistique",
          "Éducation",
          "Autre"
        ],
        required: true
      },
      {
        id: "employees",
        question: "Nombre d'employés",
        type: "select",
        options: ["1-25 employés", "25-49 employés", "50-100 employés", "100-250 employés", "250+ employés"],
        required: true
      },
      {
        id: "sites",
        question: "Nombre de sites / bureaux",
        type: "select",
        options: ["1 site unique", "2-3 sites", "4-10 sites", "10+ sites", "Multi-pays"],
        required: true
      },
      {
        id: "annual_it_budget",
        question: "Budget TI annuel (approximatif)",
        type: "select",
        options: ["Moins de 50K$", "50K$ - 100K$", "100K$ - 250K$", "250K$ - 500K$", "500K$ - 1M$", "1M$ et plus", "Non défini"],
        required: false
      }
    ]
  },
  phase2: {
    title: "Infrastructure & Services TI",
    icon: Server,
    description: "Vue d'ensemble de votre infrastructure et services TI",
    questions: [
      {
        id: "infrastructure_type",
        question: "Type d'infrastructure",
        type: "multiselect",
        options: [
          "Serveurs on-premise (sur site)",
          "Cloud public (Azure, AWS, GCP)",
          "Cloud privé",
          "Hybride (on-premise + cloud)",
          "SaaS uniquement (Microsoft 365, etc.)",
          "Hébergement tiers"
        ],
        required: true
      },
      {
        id: "main_solutions",
        question: "Principales solutions en place",
        type: "multiselect",
        options: [
          "Microsoft 365 (E3/E5/Premium)",
          "ERP (SAP, Dynamics, Oracle, etc.)",
          "CRM (Salesforce, Dynamics, HubSpot)",
          "Comptabilité (QuickBooks, Sage, etc.)",
          "Gestion documentaire / ECM",
          "Collaboration (Teams, Slack, etc.)",
          "VPN / Accès distant",
          "Backup / Reprise",
          "Firewall / Sécurité périmétrique",
          "Antivirus / EDR",
          "Monitoring / RMM"
        ],
        required: true
      },
      {
        id: "it_team",
        question: "Ressources TI actuelles",
        type: "select",
        options: [
          "Aucune ressource TI dédiée",
          "Support externe uniquement",
          "1 technicien interne + support externe",
          "2-3 personnes internes",
          "Équipe TI complète (4+ personnes)",
          "Direction TI senior en place"
        ],
        required: true
      },
      {
        id: "documentation_level",
        question: "Niveau de documentation TI",
        type: "select",
        options: [
          "Aucune documentation",
          "Documentation minimale (dispersée)",
          "Documentation partielle (non à jour)",
          "Bonne documentation (structurée)",
          "Excellente documentation (centralisée, à jour)"
        ],
        required: true
      },
      {
        id: "change_management",
        question: "Gestion des changements TI",
        type: "select",
        options: [
          "Aucun processus formel",
          "Informel (verbal seulement)",
          "Processus basique (tickets)",
          "Processus structuré (approbations)",
          "Processus mature (ITIL-aligné, CAB)"
        ],
        required: true
      },
      {
        id: "incident_management",
        question: "Gestion des incidents et pannes",
        type: "select",
        options: [
          "Réactif - aucun suivi formel",
          "Tickets manuels (email, téléphone)",
          "Système de tickets basique",
          "Système de tickets avec SLA",
          "Processus ITIL complet avec analyse post-mortem"
        ],
        required: true
      },
      {
        id: "backup_recovery",
        question: "Sauvegarde et reprise",
        type: "select",
        options: [
          "Aucune sauvegarde formelle",
          "Sauvegardes ad-hoc (non testées)",
          "Sauvegardes régulières (non testées)",
          "Sauvegardes régulières + tests occasionnels",
          "Plan DR complet + tests réguliers"
        ],
        required: true
      }
    ]
  },
  phase3: {
    title: "Gouvernance & Alignement TI",
    icon: CheckCircle2,
    description: "Processus de gouvernance, alignement stratégique et mesure de performance",
    questions: [
      {
        id: "it_committee",
        question: "Comité de gouvernance TI",
        type: "select",
        options: [
          "Aucun comité TI",
          "Réunions ad-hoc (sans structure)",
          "Comité informel (annuel)",
          "Comité formel (semestriel)",
          "Comité structuré (trimestriel avec KPIs)"
        ],
        required: true
      },
      {
        id: "strategic_alignment",
        question: "Alignement stratégique TI-Affaires",
        type: "select",
        options: [
          "Aucun alignement - TI réactif uniquement",
          "Alignement minimal - TI supporte l'opérationnel",
          "Alignement partiel - TI consulté occasionnellement",
          "Bon alignement - TI impliqué dans la stratégie",
          "Excellent alignement - TI co-crée la stratégie"
        ],
        required: true
      },
      {
        id: "it_roadmap",
        question: "Plan directeur / Roadmap TI",
        type: "select",
        options: [
          "Aucun plan TI",
          "Liste de souhaits non priorisée",
          "Plan informel (1 an)",
          "Roadmap structurée (1-2 ans)",
          "Plan directeur complet (3-5 ans avec budget)"
        ],
        required: true
      },
      {
        id: "performance_tracking",
        question: "Mesure de la performance TI",
        type: "select",
        options: [
          "Aucune mesure",
          "Mesures ad-hoc (temps d'arrêt)",
          "KPIs basiques (disponibilité)",
          "KPIs structurés (tableau de bord)",
          "Balanced Scorecard TI complet"
        ],
        required: true
      },
      {
        id: "budget_management",
        question: "Gestion budgétaire TI",
        type: "select",
        options: [
          "Pas de budget TI défini",
          "Budget réactif (dépenses au besoin)",
          "Budget annuel basique",
          "Budget structuré avec catégories",
          "Budget pluriannuel avec ROI et business case"
        ],
        required: true
      }
    ]
  },
  phase4: {
    title: "Sécurité & Conformité",
    icon: Shield,
    description: "Évaluation de la posture de sécurité et conformité réglementaire",
    questions: [
      {
        id: "security_posture",
        question: "Posture de sécurité globale",
        type: "select",
        options: [
          "Niveau 1 - Sécurité minimale (risque élevé)",
          "Niveau 2 - Sécurité basique (outils de base)",
          "Niveau 3 - Sécurité intermédiaire (bonnes pratiques partielles)",
          "Niveau 4 - Bonne sécurité (cadre structuré)",
          "Niveau 5 - Excellente sécurité (NIST/ISO aligné)"
        ],
        required: true
      },
      {
        id: "identity_access",
        question: "Gestion des identités et accès",
        type: "select",
        options: [
          "Mots de passe simples partagés",
          "Comptes individuels sans MFA",
          "MFA partiel (admin uniquement)",
          "MFA généralisé + SSO",
          "Zero Trust avec gestion des privilèges (PAM)"
        ],
        required: true
      },
      {
        id: "data_protection",
        question: "Protection des données",
        type: "select",
        options: [
          "Aucun chiffrement",
          "Chiffrement partiel (transport uniquement)",
          "Chiffrement standard (repos + transport)",
          "Chiffrement avancé + DLP basique",
          "Chiffrement complet + DLP mature + classification"
        ],
        required: true
      },
      {
        id: "threat_detection",
        question: "Détection et surveillance des menaces",
        type: "select",
        options: [
          "Aucune surveillance",
          "Antivirus basique seulement",
          "EDR + logs basiques",
          "SIEM + SOC externe",
          "SOC 24/7 + Threat Intelligence + Hunting"
        ],
        required: true
      },
      {
        id: "incident_response",
        question: "Plan de réponse aux incidents de sécurité",
        type: "select",
        options: [
          "Aucun plan",
          "Procédure informelle (non documentée)",
          "Plan basique (non testé)",
          "Plan documenté + tests annuels",
          "Plan mature (NIST-aligné) + tests trimestriels + simulations"
        ],
        required: true
      },
      {
        id: "compliance",
        question: "Conformité réglementaire",
        type: "multiselect",
        options: [
          "Loi 25 (Québec) - Protection des renseignements personnels",
          "RGPD (Europe)",
          "HIPAA (Santé - USA)",
          "SOC 2 / ISO 27001",
          "PCI-DSS (Paiements par carte)",
          "Aucune exigence spécifique",
          "Non évalué / Incertain"
        ],
        required: true
      },
      {
        id: "security_training",
        question: "Formation en cybersécurité",
        type: "select",
        options: [
          "Aucune formation",
          "Sensibilisation ad-hoc",
          "Formation annuelle basique",
          "Programme structuré + simulations phishing",
          "Programme continu + certifications + culture sécurité"
        ],
        required: true
      },
      {
        id: "vulnerabilities",
        question: "Gestion des vulnérabilités",
        type: "select",
        options: [
          "Aucune gestion",
          "Patches réactifs (après incident)",
          "Patches mensuels manuels",
          "Patches automatisés + scanning trimestriel",
          "Gestion proactive + scanning continu + pentests réguliers"
        ],
        required: true
      }
    ]
  },
  phase5: {
    title: "Infrastructure Matérielle Détaillée",
    icon: Server,
    description: "Inventaire complet des serveurs et équipements",
    questions: [
      {
        id: "server_count",
        question: "Nombre total de serveurs (physiques + virtuels)",
        type: "number",
        required: true,
        placeholder: "Ex: 15"
      },
      {
        id: "server_inventory_text",
        question: "Inventaire des serveurs principaux (Nom, Rôle, OS, Âge, Specs)",
        type: "textarea",
        rows: 8,
        required: false,
        placeholder: "Ex:\nSRV-DC01 | Contrôleur domaine | Windows Server 2019 | 3 ans | 8 cores, 32GB RAM\nSRV-SQL01 | Base de données | SQL Server 2019 | 2 ans | 16 cores, 64GB RAM\n..."
      },
      {
        id: "virtualization_platform",
        question: "Plateforme de virtualisation",
        type: "select",
        options: [
          "Aucune virtualisation",
          "Hyper-V",
          "VMware vSphere/ESXi",
          "Proxmox",
          "KVM",
          "Autre"
        ],
        required: true
      },
      {
        id: "network_equipment_inventory",
        question: "Inventaire des équipements réseau (Switches, Firewalls, etc.)",
        type: "textarea",
        rows: 6,
        required: false,
        placeholder: "Ex:\nCisco SG350-28 | Switch coeur | 28 ports | 5 ans\nFortiGate 60F | Firewall principal | 2 ans\n..."
      },
      {
        id: "server_room_location",
        question: "Localisation de la salle serveurs",
        type: "text",
        required: false,
        placeholder: "Ex: Sous-sol bureau principal, Montréal"
      },
      {
        id: "server_room_security",
        question: "Sécurité physique de la salle serveurs",
        type: "multiselect",
        options: [
          "Accès restreint (badge/clé)",
          "Caméras de surveillance",
          "Détecteur de fumée",
          "Système de suppression incendie",
          "Climatisation dédiée",
          "UPS (onduleur)",
          "Générateur de secours",
          "Aucune mesure spécifique"
        ],
        required: true
      },
      {
        id: "infrastructure_age",
        question: "Âge moyen de l'infrastructure matérielle",
        type: "select",
        options: [
          "Moins de 2 ans",
          "2-3 ans",
          "3-5 ans",
          "5-7 ans",
          "7-10 ans",
          "Plus de 10 ans",
          "Mixte (varie beaucoup)"
        ],
        required: true
      },
      {
        id: "replacement_cycle",
        question: "Cycle de remplacement matériel",
        type: "select",
        options: [
          "Aucun cycle défini",
          "À la panne uniquement",
          "Tous les 5 ans",
          "Tous les 3-4 ans",
          "Planification budgétaire annuelle"
        ],
        required: true
      }
    ]
  },
  phase6: {
    title: "Applications & Solutions",
    icon: Server,
    description: "Détails sur les applications critiques et licences",
    questions: [
      {
        id: "erp_solution",
        question: "Solution ERP (si applicable)",
        type: "text",
        required: false,
        placeholder: "Ex: SAP Business One, Dynamics 365, Odoo, NetSuite..."
      },
      {
        id: "crm_solution",
        question: "Solution CRM (si applicable)",
        type: "text",
        required: false,
        placeholder: "Ex: Salesforce, HubSpot, Dynamics 365 Sales, Pipedrive..."
      },
      {
        id: "accounting_solution",
        question: "Solution comptable/finance",
        type: "text",
        required: false,
        placeholder: "Ex: QuickBooks, Sage 50, Acomba, Dynamics GP..."
      },
      {
        id: "microsoft365_plan",
        question: "Plan Microsoft 365 (si applicable)",
        type: "select",
        options: [
          "Non utilisé",
          "Microsoft 365 Business Basic",
          "Microsoft 365 Business Standard",
          "Microsoft 365 Business Premium",
          "Microsoft 365 E3",
          "Microsoft 365 E5",
          "Autre/Mixte"
        ],
        required: true
      },
      {
        id: "critical_apps_inventory",
        question: "Top 10 applications critiques (Nom | Éditeur | Nb utilisateurs | Criticité 1-5)",
        type: "textarea",
        rows: 12,
        required: false,
        placeholder: "Ex:\n1. ERP Sage X3 | Sage | 45 users | Criticité 5\n2. CRM Salesforce | Salesforce | 25 users | Criticité 4\n3. Email Exchange | Microsoft | 50 users | Criticité 5\n..."
      },
      {
        id: "app_integration",
        question: "Niveau d'intégration entre les systèmes",
        type: "select",
        options: [
          "Aucune intégration - systèmes isolés",
          "Intégration minimale - exports/imports manuels",
          "Intégration partielle - quelques connecteurs",
          "Bonne intégration - APIs et middleware",
          "Excellente intégration - plateforme unifiée"
        ],
        required: true
      },
      {
        id: "cloud_vs_onprem_ratio",
        question: "Répartition Cloud vs On-Premise",
        type: "select",
        options: [
          "100% On-Premise",
          "75% On-Premise / 25% Cloud",
          "50% On-Premise / 50% Cloud",
          "25% On-Premise / 75% Cloud",
          "100% Cloud (SaaS uniquement)"
        ],
        required: true
      },
      {
        id: "annual_licensing_cost",
        question: "Coût annuel total des licences logicielles (estimation)",
        type: "select",
        options: [
          "Moins de 10K$",
          "10K$ - 25K$",
          "25K$ - 50K$",
          "50K$ - 100K$",
          "100K$ - 250K$",
          "Plus de 250K$",
          "Non connu"
        ],
        required: false
      }
    ]
  },
  phase7: {
    title: "Réseau & Télécommunications",
    icon: Server,
    description: "Infrastructure réseau et connectivité",
    questions: [
      {
        id: "internet_provider_primary",
        question: "Fournisseur Internet principal",
        type: "text",
        required: false,
        placeholder: "Ex: Bell Canada, Videotron, Rogers..."
      },
      {
        id: "internet_bandwidth_primary",
        question: "Bande passante Internet principale",
        type: "select",
        options: [
          "Moins de 50 Mbps",
          "50-100 Mbps",
          "100-250 Mbps",
          "250-500 Mbps",
          "500 Mbps - 1 Gbps",
          "1 Gbps et plus",
          "Non connu"
        ],
        required: true
      },
      {
        id: "internet_redundancy",
        question: "Redondance Internet (lien secondaire)",
        type: "select",
        options: [
          "Aucune redondance",
          "Lien secondaire même fournisseur",
          "Lien secondaire fournisseur différent",
          "Lien LTE/5G de backup",
          "Multi-homing avec BGP"
        ],
        required: true
      },
      {
        id: "wan_topology",
        question: "Topologie WAN (multi-sites)",
        type: "select",
        options: [
          "Site unique - non applicable",
          "VPN site-à-site (IPSEC)",
          "MPLS",
          "SD-WAN",
          "VPN + MPLS hybride",
          "Autre"
        ],
        required: true
      },
      {
        id: "vlan_segmentation",
        question: "Segmentation réseau (VLANs)",
        type: "select",
        options: [
          "Aucune segmentation - réseau plat",
          "Segmentation basique (2-3 VLANs)",
          "Segmentation moyenne (4-10 VLANs)",
          "Segmentation avancée (10+ VLANs)",
          "Micro-segmentation"
        ],
        required: true
      },
      {
        id: "wifi_infrastructure",
        question: "Infrastructure Wi-Fi",
        type: "multiselect",
        options: [
          "Pas de Wi-Fi",
          "Points d'accès autonomes",
          "Contrôleur Wi-Fi centralisé",
          "Wi-Fi managé cloud",
          "SSID séparés (invités, employés)",
          "Authentification 802.1X",
          "WPA3"
        ],
        required: true
      },
      {
        id: "vpn_solution",
        question: "Solution VPN pour accès distant",
        type: "text",
        required: false,
        placeholder: "Ex: FortiClient, Cisco AnyConnect, OpenVPN, WireGuard..."
      },
      {
        id: "vpn_users_count",
        question: "Nombre d'utilisateurs VPN actifs",
        type: "select",
        options: [
          "Aucun",
          "1-10 utilisateurs",
          "11-25 utilisateurs",
          "26-50 utilisateurs",
          "50+ utilisateurs"
        ],
        required: false
      }
    ]
  },
  phase8: {
    title: "Équipe & Ressources Humaines",
    icon: Building2,
    description: "Personnel TI et compétences",
    questions: [
      {
        id: "it_team_count",
        question: "Nombre total de personnes dédiées TI (internes)",
        type: "number",
        required: true,
        placeholder: "Ex: 3"
      },
      {
        id: "it_team_roles",
        question: "Rôles et responsabilités de l'équipe TI",
        type: "textarea",
        rows: 6,
        required: false,
        placeholder: "Ex:\n- Directeur TI (1) - Stratégie, gouvernance\n- Technicien support (2) - Helpdesk, maintenance\n- Administrateur systèmes (1) - Serveurs, réseau\n..."
      },
      {
        id: "it_manager_level",
        question: "Niveau du responsable TI",
        type: "select",
        options: [
          "Aucun responsable TI dédié",
          "Technicien senior",
          "Gestionnaire TI",
          "Directeur TI",
          "VP/CIO"
        ],
        required: true
      },
      {
        id: "external_it_provider",
        question: "Fournisseur TI externe / MSP (si applicable)",
        type: "text",
        required: false,
        placeholder: "Ex: Nom du MSP, contrat support, etc."
      },
      {
        id: "external_it_support_type",
        question: "Type de support externe",
        type: "select",
        options: [
          "Aucun support externe",
          "Support ponctuel (projets)",
          "Support réactif (à la demande)",
          "Contrat support récurrent",
          "MSP complet (managed services)"
        ],
        required: true
      },
      {
        id: "it_skills_gaps",
        question: "Lacunes de compétences identifiées",
        type: "multiselect",
        options: [
          "Cybersécurité",
          "Cloud (Azure/AWS/GCP)",
          "Réseaux avancés",
          "Développement/DevOps",
          "Gestion de projet",
          "Architecture d'entreprise",
          "Gouvernance TI",
          "Business Intelligence/Analytics",
          "Aucune lacune majeure"
        ],
        required: false
      },
      {
        id: "it_training_budget",
        question: "Budget annuel formation TI",
        type: "select",
        options: [
          "Aucun budget formation",
          "Moins de 5K$",
          "5K$ - 10K$",
          "10K$ - 25K$",
          "25K$ - 50K$",
          "Plus de 50K$"
        ],
        required: false
      },
      {
        id: "it_turnover",
        question: "Turnover de l'équipe TI (3 dernières années)",
        type: "select",
        options: [
          "Aucun départ",
          "1-2 départs",
          "3-5 départs",
          "Plus de 5 départs",
          "Turnover élevé (problématique)"
        ],
        required: false
      }
    ]
  },
  phase9: {
    title: "Financier & Contractuel",
    icon: Building2,
    description: "Budget, dépenses et contrats TI",
    questions: [
      {
        id: "annual_it_budget_exact",
        question: "Budget TI annuel total (estimation précise si possible)",
        type: "text",
        required: false,
        placeholder: "Ex: 275000$ ou 200K$ - 300K$"
      },
      {
        id: "it_budget_breakdown",
        question: "Répartition budgétaire TI",
        type: "textarea",
        rows: 8,
        required: false,
        placeholder: "Ex:\nCAPEX (Infrastructure): 30%\nOPEX (Licences): 25%\nOPEX (Support/MSP): 20%\nOPEX (Salaires): 25%"
      },
      {
        id: "major_contracts",
        question: "Contrats majeurs en cours (Fournisseur | Objet | Échéance | Coût annuel)",
        type: "textarea",
        rows: 8,
        required: false,
        placeholder: "Ex:\nMicrosoft | M365 E3 (50 licences) | Mensuel | 18K$/an\nVMware | vSphere Essentials | 2026-03 | 5K$/an\nMSP XYZ | Support 24/7 | 2025-12 | 48K$/an\n..."
      },
      {
        id: "capex_opex_ratio",
        question: "Répartition CAPEX vs OPEX",
        type: "select",
        options: [
          "Majoritairement CAPEX (70%+)",
          "Équilibré CAPEX/OPEX (50/50)",
          "Majoritairement OPEX (70%+)",
          "100% OPEX (tout cloud/SaaS)",
          "Non suivi"
        ],
        required: false
      },
      {
        id: "projects_in_progress",
        question: "Projets TI en cours (Description | Budget | Échéance)",
        type: "textarea",
        rows: 6,
        required: false,
        placeholder: "Ex:\nMigration vers Azure | 75K$ | Q2 2025\nDéploiement EDR | 25K$ | Q1 2025\n..."
      },
      {
        id: "planned_projects_12months",
        question: "Projets TI planifiés (12 prochains mois)",
        type: "textarea",
        rows: 6,
        required: false,
        placeholder: "Ex:\nRemplacement serveurs vieillissants\nMigration vers M365 E5\nDéploiement MFA généralisé\n..."
      },
      {
        id: "available_budget_initiatives",
        question: "Budget disponible pour nouvelles initiatives",
        type: "select",
        options: [
          "Aucun budget disponible",
          "Moins de 25K$",
          "25K$ - 50K$",
          "50K$ - 100K$",
          "100K$ - 250K$",
          "250K$ - 500K$",
          "Plus de 500K$",
          "À déterminer selon projet"
        ],
        required: false
      }
    ]
  },
  phase10: {
    title: "Objectifs & Vision",
    icon: Target,
    description: "Stratégie, objectifs et priorités",
    questions: [
      {
        id: "business_objectives_detail",
        question: "Objectifs d'affaires prioritaires (détails)",
        type: "textarea",
        rows: 6,
        required: true,
        placeholder: "Ex:\n- Croissance 25% revenus d'ici 2026\n- Expansion géographique (2 nouveaux sites)\n- Digitalisation complète des processus\n- Amélioration expérience client..."
      },
      {
        id: "it_strategic_priorities",
        question: "3 priorités stratégiques TI pour les 12 prochains mois",
        type: "textarea",
        rows: 5,
        required: true,
        placeholder: "1. ...\n2. ...\n3. ..."
      },
      {
        id: "main_pain_points",
        question: "Top 5 pain points TI actuels (en ordre de priorité)",
        type: "textarea",
        rows: 6,
        required: true,
        placeholder: "1. Pannes fréquentes impactant la productivité\n2. Manque de visibilité sur l'infrastructure\n3. Processus manuels chronophages\n4. Inquiétudes cybersécurité\n5. Coûts TI difficiles à justifier..."
      },
      {
        id: "digital_transformation_maturity",
        question: "Maturité transformation numérique",
        type: "select",
        options: [
          "Niveau 1 - Traditionnelle (papier/manuel)",
          "Niveau 2 - Numérisée (outils de base)",
          "Niveau 3 - Digitale (processus automatisés)",
          "Niveau 4 - Transformée (data-driven)",
          "Niveau 5 - Innovante (IA, analytics avancés)"
        ],
        required: true
      },
      {
        id: "competitive_positioning",
        question: "Positionnement TI vs compétiteurs",
        type: "select",
        options: [
          "En retard significatif",
          "Légèrement en retard",
          "À parité",
          "Légèrement en avance",
          "Leader technologique",
          "Non évalué"
        ],
        required: false
      },
      {
        id: "success_criteria",
        question: "Critères de succès pour l'initiative TI (mesurables)",
        type: "textarea",
        rows: 6,
        required: true,
        placeholder: "Ex:\n- Réduction 80% des incidents système\n- Disponibilité 99.5%+\n- ROI positif dans les 18 mois\n- Satisfaction employés +40%\n- Conformité Loi 25 à 100%..."
      },
      {
        id: "timeline_expectations",
        question: "Échéancier souhaité pour résultats",
        type: "select",
        options: [
          "Résultats immédiats (0-3 mois)",
          "Court terme (3-6 mois)",
          "Moyen terme (6-12 mois)",
          "Long terme (12-24 mois)",
          "Vision 3-5 ans"
        ],
        required: true
      },
      {
        id: "additional_comments",
        question: "Commentaires additionnels ou contexte important",
        type: "textarea",
        rows: 6,
        required: false,
        placeholder: "Tout autre information pertinente que nous devrions connaître..."
      }
    ]
  }
};

// 🏢 DONNÉES MOCKUP - Cabinet d'avocats 50 employés
const MOCKUP_DATA: Record<string, string | string[]> = {
  // Phase 1: Profil
  company_name: "Tremblay, Gagnon & Associés",
  industry: "Cabinet d'avocats",
  employees: "50-100 employés",
  sites: "1 site unique",
  annual_it_budget: "100K$ - 250K$",
  
  // Phase 2: Infrastructure
  infrastructure_type: [
    "Serveurs on-premise (sur site)",
    "Cloud public (Azure, AWS, GCP)",
    "SaaS uniquement (Microsoft 365, etc.)"
  ],
  main_solutions: [
    "Microsoft 365 (E3/E5/Premium)",
    "Comptabilité (QuickBooks, Sage, etc.)",
    "Gestion documentaire / ECM",
    "Collaboration (Teams, Slack, etc.)",
    "VPN / Accès distant",
    "Backup / Reprise",
    "Firewall / Sécurité périmétrique",
    "Antivirus / EDR"
  ],
  it_team: "1 technicien interne + support externe",
  documentation_level: "Documentation partielle (non à jour)",
  change_management: "Processus basique (tickets)",
  incident_management: "Système de tickets basique",
  backup_recovery: "Sauvegardes régulières + tests occasionnels",
  
  // Phase 3: Gouvernance
  it_committee: "Comité informel (annuel)",
  strategic_alignment: "Alignement partiel - TI consulté occasionnellement",
  it_roadmap: "Plan informel (1 an)",
  performance_tracking: "KPIs basiques (disponibilité)",
  budget_management: "Budget annuel basique",
  
  // Phase 4: Sécurité
  security_posture: "Niveau 3 - Sécurité intermédiaire (bonnes pratiques partielles)",
  identity_access: "MFA partiel (admin uniquement)",
  data_protection: "Chiffrement standard (repos + transport)",
  threat_detection: "EDR + logs basiques",
  incident_response: "Plan basique (non testé)",
  compliance: [
    "Loi 25 (Québec) - Protection des renseignements personnels",
    "Aucune exigence spécifique"
  ],
  security_training: "Sensibilisation ad-hoc",
  vulnerabilities: "Patches mensuels manuels",
  
  // Phase 5: Infrastructure Matérielle
  server_count: "8",
  server_inventory_text: `SRV-DC01 | Contrôleur domaine AD | Windows Server 2019 | 4 ans | 4 cores, 16GB RAM
SRV-FILE01 | Serveur fichiers | Windows Server 2022 | 2 ans | 4 cores, 16GB RAM
SRV-SQL01 | Base de données | SQL Server 2019 | 3 ans | 8 cores, 32GB RAM
SRV-TERM01 | Terminal Server | Windows Server 2019 | 4 ans | 8 cores, 32GB RAM`,
  virtualization_platform: "Hyper-V",
  network_equipment_inventory: `Cisco SG350-28 | Switch coeur | 28 ports | 5 ans
FortiGate 60F | Firewall | 2 ans
Ubiquiti UAP-AC-Pro | Points d'accès Wi-Fi (5) | 3 ans`,
  server_room_location: "Salle informatique, 3e étage",
  server_room_security: [
    "Accès restreint (badge/clé)",
    "Climatisation dédiée",
    "UPS (onduleur)"
  ],
  infrastructure_age: "3-5 ans",
  replacement_cycle: "À la panne uniquement",
  
  // Phase 6: Applications
  erp_solution: "Non applicable",
  crm_solution: "Non applicable",
  accounting_solution: "QuickBooks Enterprise",
  microsoft365_plan: "Microsoft 365 Business Premium",
  critical_apps_inventory: `1. Email Exchange Online | Microsoft | 50 users | Criticité 5
2. Gestion documentaire iManage | iManage | 45 users | Criticité 5
3. QuickBooks Enterprise | Intuit | 5 users | Criticité 4
4. PCLaw | LexisNexis | 40 users | Criticité 5`,
  app_integration: "Intégration minimale - exports/imports manuels",
  cloud_vs_onprem_ratio: "50% On-Premise / 50% Cloud",
  annual_licensing_cost: "50K$ - 100K$",
  
  // Phase 7: Réseau
  internet_provider_primary: "Bell Canada",
  internet_bandwidth_primary: "250-500 Mbps",
  internet_redundancy: "Aucune redondance",
  wan_topology: "Site unique - non applicable",
  vlan_segmentation: "Segmentation basique (2-3 VLANs)",
  wifi_infrastructure: [
    "Points d'accès autonomes",
    "SSID séparés (invités, employés)"
  ],
  vpn_solution: "FortiClient VPN",
  vpn_users_count: "11-25 utilisateurs",
  
  // Phase 8: Équipe RH
  it_team_count: "1",
  it_team_roles: "Technicien TI (1) - Support niveau 1-2, maintenance infrastructure basique",
  it_manager_level: "Technicien senior",
  external_it_provider: "TechnoConseil Inc. - Support ponctuel",
  external_it_support_type: "Support ponctuel (projets)",
  it_skills_gaps: [
    "Cybersécurité",
    "Cloud (Azure/AWS/GCP)",
    "Gouvernance TI"
  ],
  it_training_budget: "Moins de 5K$",
  it_turnover: "Aucun départ",
  
  // Phase 9: Financier
  annual_it_budget_exact: "150000$",
  it_budget_breakdown: `CAPEX (Infrastructure): 20%
OPEX (Licences M365, logiciels): 35%
OPEX (Support externe): 15%
OPEX (Salaire technicien): 30%`,
  major_contracts: `Microsoft | M365 Business Premium (50 licences) | Mensuel | 42K$/an
iManage | Work 10 (45 licences) | Annuel | 28K$/an
Bell Canada | Internet 300 Mbps | Mensuel | 1.2K$/an`,
  capex_opex_ratio: "Majoritairement OPEX (70%+)",
  projects_in_progress: "",
  planned_projects_12months: `Remplacement serveurs vieillissants (DC01, TERM01)
Déploiement MFA pour tous les utilisateurs
Amélioration sauvegarde offsite`,
  available_budget_initiatives: "25K$ - 50K$",
  
  // Phase 10: Objectifs
  business_objectives_detail: `- Croissance 15% du portefeuille clients d'ici 2026
- Amélioration satisfaction client (enquêtes positives 85%+)
- Digitalisation complète du dossier client (zéro papier)
- Réduction délais traitement dossiers de 20%`,
  it_strategic_priorities: `1. Moderniser infrastructure serveurs (EOL Windows Server 2019)
2. Renforcer cybersécurité (MFA, EDR, formation)
3. Améliorer collaboration et mobilité (Teams, SharePoint)`,
  main_pain_points: `1. Lenteurs système en fin de journée (performances)
2. Inquiétude cyberattaques (ransomware, phishing)
3. Difficulté accès documents à distance
4. Coûts TI élevés sans visibilité claire
5. Manque expertise interne pour projets stratégiques`,
  digital_transformation_maturity: "Niveau 2 - Numérisée (outils de base)",
  competitive_positioning: "Légèrement en retard",
  success_criteria: `- Réduction 50% des plaintes performance
- Zéro incident sécurité majeur
- Disponibilité 99% des systèmes critiques
- Conformité Loi 25 à 100%
- Satisfaction utilisateurs 85%+`,
  timeline_expectations: "Moyen terme (6-12 mois)",
  additional_comments: "Budget limité mais conscients de l'importance d'investir dans la cybersécurité"
};

export default function DiagnosticTIWizard({ isOpen, onClose }: DiagnosticTIWizardProps) {
  // 💾 Charger les données sauvegardées ou utiliser MOCKUP_DATA
  const loadSavedData = () => {
    try {
      const saved = localStorage.getItem('diagnosticTI_responses');
      return saved ? JSON.parse(saved) : MOCKUP_DATA;
    } catch (e) {
      return MOCKUP_DATA;
    }
  };

  const [currentPhase, setCurrentPhase] = useState<keyof typeof QUESTIONS>('phase1');
  const [responses, setResponses] = useState<Record<string, string | string[]>>(loadSavedData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const [generatedDocs, setGeneratedDocs] = useState<GeneratedDocuments | null>(null);
  const [showDocumentSelection, setShowDocumentSelection] = useState(false);
  
  const phases = Object.keys(QUESTIONS) as Array<keyof typeof QUESTIONS>;
  const currentPhaseIndex = phases.indexOf(currentPhase);
  const currentPhaseData = QUESTIONS[currentPhase];
  const progress = ((currentPhaseIndex + 1) / phases.length) * 100;

  // 💾 Sauvegarder automatiquement les réponses
  React.useEffect(() => {
    try {
      localStorage.setItem('diagnosticTI_responses', JSON.stringify(responses));
    } catch (e) {
      console.error('Erreur sauvegarde LocalStorage:', e);
    }
  }, [responses]);

  const handleResponse = (questionId: string, value: string | string[]) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const isPhaseComplete = () => {
    const requiredQuestions = currentPhaseData.questions.filter(q => q.required);
    return requiredQuestions.every(q => {
      const response = responses[q.id];
      if (Array.isArray(response)) {
        return response.length > 0;
      }
      return response && response.trim() !== '';
    });
  };

  const handleNext = () => {
    if (currentPhaseIndex < phases.length - 1) {
      setCurrentPhase(phases[currentPhaseIndex + 1]);
    }
    // Après la phase 4, on n'appelle plus handleGenerate() automatiquement
    // L'utilisateur choisira quel document générer
  };

  const handleBack = () => {
    if (currentPhaseIndex > 0) {
      setCurrentPhase(phases[currentPhaseIndex - 1]);
    }
  };

  // handleGenerate n'est plus utilisé - les documents sont générés via les boutons séparés

  const renderQuestion = (question: any) => {
    const value = responses[question.id] || '';

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            placeholder={question.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={question.rows || 4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent resize-y"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            placeholder={question.placeholder}
            min={question.min || 0}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent"
          />
        );

      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent bg-white"
          >
            <option value="">-- Sélectionnez --</option>
            {question.options.map((opt: string) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
            {question.options.map((opt: string) => {
              const selected = Array.isArray(value) && value.includes(opt);
              
              return (
                <label
                  key={opt}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selected 
                      ? 'border-bz-blue bg-bz-blue/5' 
                      : 'border-gray-300 hover:border-bz-blue/50 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      const newValues = e.target.checked
                        ? [...currentValues, opt]
                        : currentValues.filter(v => v !== opt);
                      handleResponse(question.id, newValues);
                    }}
                    className="mt-1"
                  />
                  <span className="text-gray-700 text-sm">{opt}</span>
                </label>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Afficher l'écran de sélection de document
  if (showDocumentSelection && !isGenerating && !generatedDocs) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              ✅ Évaluation complétée!
            </h2>
            <p className="text-gray-600 text-lg">
              Choisissez le document à générer (15-20 minutes par document)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Bouton Bilan de Santé */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setIsGenerating(true);
                setGenerationProgress(0);
                generateBilanSanteTIBySection(responses, (section, step) => {
                  const progress = (section / 8) * 100;
                  setGenerationProgress(progress);
                  setGenerationStep(`Section ${section}/8: ${step}`);
                }).then(bilanSante => {
                  setGeneratedDocs({ bilanSante, auditSecurite: '' });
                  setIsGenerating(false);
                }).catch(error => {
                  console.error('Erreur:', error);
                  alert('Erreur lors de la génération. Veuillez réessayer.');
                  setIsGenerating(false);
                });
              }}
              className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="relative z-10">
                <div className="text-5xl mb-4">📊</div>
                <h3 className="text-2xl font-bold mb-2">Bilan de Santé TI</h3>
                <p className="text-blue-100 text-sm mb-4">
                  COBIT 2019 + ITIL 4 + ISO 20000
                </p>
                <p className="text-blue-200 text-xs">
                  ~50 pages • 8 sections • 15-20 min
                </p>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/10"></div>
            </motion.button>

            {/* Bouton Audit de Sécurité */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setIsGenerating(true);
                setGenerationProgress(0);
                generateAuditSecuriteBySection(responses, (section, step) => {
                  const progress = (section / 8) * 100;
                  setGenerationProgress(progress);
                  setGenerationStep(`Section ${section}/8: ${step}`);
                }).then(auditSecurite => {
                  setGeneratedDocs({ bilanSante: '', auditSecurite });
                  setIsGenerating(false);
                }).catch(error => {
                  console.error('Erreur:', error);
                  alert('Erreur lors de la génération. Veuillez réessayer.');
                  setIsGenerating(false);
                });
              }}
              className="relative overflow-hidden bg-gradient-to-br from-red-500 to-orange-600 text-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="relative z-10">
                <div className="text-5xl mb-4">🛡️</div>
                <h3 className="text-2xl font-bold mb-2">Audit de Sécurité</h3>
                <p className="text-red-100 text-sm mb-4">
                  NIST CSF 2.0 + ISO 27001 + CIS v8
                </p>
                <p className="text-red-200 text-xs">
                  ~50 pages • 8 sections • 15-20 min
                </p>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/10"></div>
            </motion.button>
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowDocumentSelection(false)}
              className="text-gray-500 hover:text-gray-700 underline"
            >
              ← Retour au questionnaire
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Afficher l'écran de génération
  if (isGenerating && !generatedDocs) {
    return (
      <GenerationProgress 
        currentStep={generationStep} 
        progress={generationProgress}
        documentLabels={{
          doc1: 'Bilan de santé TI',
          doc2: 'Audit de sécurité'
        }}
      />
    );
  }

  // Afficher le viewer de documents
  if (generatedDocs) {
    return (
      <DiagnosticDocumentViewer
        documents={generatedDocs}
        companyName={responses.company_name as string || 'Votre organisation'}
        onClose={() => {
          setGeneratedDocs(null);
          setIsGenerating(false);
          onClose();
        }}
      />
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-bz-blue to-cyan-600 text-white p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">Diagnostic TI Complet</h2>
                <p className="text-white/90">Évaluez la maturité TI et générez 2 documents normés</p>
                <p className="text-white/70 text-sm mt-1">Bilan de santé (COBIT/ITIL) + Audit sécurité (NIST/ISO 27001)</p>
                <div className="flex gap-3 mt-2 text-xs text-white/60">
                  <span className="flex items-center gap-1">
                    💾 Sauvegarde automatique
                  </span>
                  <button
                    onClick={() => {
                      if (confirm('Voulez-vous vraiment réinitialiser toutes les réponses ?')) {
                        localStorage.removeItem('diagnosticTI_responses');
                        setResponses(MOCKUP_DATA);
                        setCurrentPhase('phase1');
                      }
                    }}
                    className="hover:text-white underline"
                  >
                    🔄 Réinitialiser
                  </button>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-2">
                  {React.createElement(currentPhaseData.icon, { className: "w-4 h-4" })}
                  <span className="font-medium">Phase {currentPhaseIndex + 1}/{phases.length}</span>
                  <span className="text-white/70">•</span>
                  {currentPhaseData.title}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  className="bg-white h-2"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPhase}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Description de la phase */}
                {currentPhaseData.description && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>Objectif:</strong> {currentPhaseData.description}
                    </p>
                  </div>
                )}

                {currentPhaseData.questions.map((question, idx) => (
                  <div key={question.id} className="space-y-2">
                    <label className="block font-medium text-gray-900">
                      {idx + 1}. {question.question}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderQuestion(question)}
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
            <button
              onClick={handleBack}
              disabled={currentPhaseIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Précédent
            </button>

            <button
              onClick={() => {
                if (currentPhaseIndex === phases.length - 1) {
                  setShowDocumentSelection(true);
                } else {
                  handleNext();
                }
              }}
              disabled={!isPhaseComplete()}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-bz-blue text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {currentPhaseIndex === phases.length - 1 ? (
                <>
                  <Sparkles className="w-5 h-5" />
                  Continuer
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// 🚀 FONCTIONS DE GÉNÉRATION PAR SECTIONS

// Générer le Bilan de Santé TI en 8 sections
async function generateBilanSanteTIBySection(
  responses: Record<string, string | string[]>,
  onProgress?: (section: number, step: string) => void
): Promise<string> {
  const context = buildContext(responses);
  const sections: string[] = [];
  
  const sectionPrompts = getBilanSanteSections(context, responses);
  
  for (let i = 0; i < sectionPrompts.length; i++) {
    try {
      if (onProgress) {
        onProgress(i + 1, sectionPrompts[i].title);
      }
      const sectionContent = await callOpenAI(sectionPrompts[i].prompt);
      sections.push(sectionContent);
    } catch (error) {
      console.error(`Erreur section ${i + 1}:`, error);
      sections.push(`\n\n[ERREUR: Impossible de générer la section ${sectionPrompts[i].title}]\n\n`);
    }
  }
  
  return sections.join('\n\n---\n\n');
}

// Générer l'Audit de Sécurité en 8 sections
async function generateAuditSecuriteBySection(
  responses: Record<string, string | string[]>,
  onProgress?: (section: number, step: string) => void
): Promise<string> {
  const context = buildContext(responses);
  const sections: string[] = [];
  
  const sectionPrompts = getAuditSecuriteSections(context, responses);
  
  for (let i = 0; i < sectionPrompts.length; i++) {
    try {
      if (onProgress) {
        onProgress(i + 1, sectionPrompts[i].title);
      }
      const sectionContent = await callOpenAI(sectionPrompts[i].prompt);
      sections.push(sectionContent);
    } catch (error) {
      console.error(`Erreur section ${i + 1}:`, error);
      sections.push(`\n\n[ERREUR: Impossible de générer la section ${sectionPrompts[i].title}]\n\n`);
    }
  }
  
  return sections.join('\n\n---\n\n');
}

function buildContext(responses: Record<string, string | string[]>): string {
  const formatValue = (value: string | string[]): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  return Object.entries(responses)
    .map(([key, value]) => `${key}: ${formatValue(value)}`)
    .join('\n');
}

// 📋 Définition des sections pour le Bilan de Santé TI
function getBilanSanteSections(context: string, responses: Record<string, string | string[]>) {
  const company = responses.company_name || 'Votre organisation';
  const date = new Date().toLocaleDateString('fr-FR');
  
  return [
    {
      title: "Page titre, méthodologie et sommaire exécutif",
      prompt: `Tu es un expert consultant TI chez BZ, certifié COBIT 2019, ITIL 4, ISO 20000.

GÉNÈRE LA SECTION 1/8 du Bilan de Santé TI pour: ${company}

CONTEXTE CLIENT:
${context}

CONTENU À GÉNÉRER (2500 mots):

# BILAN DE SANTÉ TI - ${company}
Date: ${date}
Consultant: Dany Bourassa, Expert TI Senior
Référence: BZ-BST-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}

## TABLE DES MATIÈRES
1. Méthodologie d'évaluation
2. Sommaire exécutif
3. Profil organisationnel
[Liste complète des 8 sections]

## 1. MÉTHODOLOGIE D'ÉVALUATION

### 1.1 Cadres normatifs utilisés
- **COBIT 2019** (40 processus: EDM, APO, BAI, DSS, MEA)
- **ITIL 4** (34 pratiques de gestion des services)
- **ISO/IEC 20000-1:2018**
- **CMMi v2.0**

### 1.2 Échelle de maturité (1-5)
| Niveau | Symbole | Description |
|--------|---------|-------------|
| 1 | 🔴 | Initial/Ad-hoc - Processus chaotiques |
| 2 | 🟠 | Reproductible - Processus de base définis |
| 3 | 🟡 | Défini - Processus standardisés et documentés |
| 4 | 🟢 | Géré - Processus mesurés et contrôlés |
| 5 | 🟦 | Optimisé - Amélioration continue, excellence |

## 2. SOMMAIRE EXÉCUTIF

### 2.1 Score global de maturité

\`\`\`
╔════════════════════════════════════════╗
║   SCORE GLOBAL DE MATURITÉ TI         ║
║         [CALCULER SCORE] / 5.0        ║
║       Niveau: [DÉTERMINER] [ÉMOJI]    ║
╚════════════════════════════════════════╝
\`\`\`

### 2.2 Spider Chart - Vue d'ensemble

\`\`\`
         Gouvernance (X/5) [ÉMOJI]
                  |
    Stratégie   /|\\  Performance
      (X/5)    / | \\    (X/5)
     [ÉMOJI]  /  |  \\  [ÉMOJI]
              /   |   \\
        Services  |  Infrastructure
         (X/5)    |     (X/5)
        [ÉMOJI]   |    [ÉMOJI]
                  |
            Documentation
              (X/5) [ÉMOJI]
\`\`\`

### 2.3 Constats principaux

**🟢 FORCES IDENTIFIÉES:**
Analyse les données réelles du questionnaire et identifie 3-5 forces concrètes de ${company}.
Base-toi sur:
- Infrastructure: ${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : responses.infrastructure_type}
- Solutions: ${Array.isArray(responses.main_solutions) ? responses.main_solutions.join(', ') : ''}
- Équipe: ${responses.it_team}
- Documentation: ${responses.documentation_level}
- Gouvernance: Comité ${responses.it_committee}, Alignement ${responses.strategic_alignment}

**🔴 FAIBLESSES CRITIQUES:**
Identifie 3-5 faiblesses RÉELLES basées sur:
- Gestion changements: ${responses.change_management}
- Gestion incidents: ${responses.incident_management}
- Backup: ${responses.backup_recovery}
- Sécurité: ${responses.security_posture}
- Âge infra: ${responses.infrastructure_age || 'Non spécifié'}

**⚠️ RISQUES MAJEURS SI INACTION:**
Décris 3-5 risques concrets basés sur les faiblesses identifiées, avec impacts $ estimés réalistes pour ${responses.industry}.

### 2.4 Top 5 recommandations prioritaires

Crée 5 recommandations CONCRÈTES basées sur les données du questionnaire. Utilise:
- Pain points: ${responses.main_pain_points || 'Non spécifiés'}
- Priorités stratégiques: ${responses.it_strategic_priorities || 'Non spécifiées'}
- Budget disponible: ${responses.available_budget_initiatives || 'Non défini'}
- Échéancier: ${responses.timeline_expectations || 'Non spécifié'}

| # | Recommandation | Priorité | Échéance | Coût estimé | Impact |
|---|----------------|----------|----------|-------------|--------|
[Génère 5 recommandations BASÉES sur les données réelles ci-dessus]

## 3. PROFIL ORGANISATIONNEL

| Attribut | Valeur |
|----------|--------|
| Organisation | ${company} |
| Secteur | ${responses.industry} |
| Taille | ${responses.employees} |
| Sites | ${responses.sites} |
| Budget TI | ${responses.annual_it_budget} |
| Ressources TI | ${responses.it_team} |

### 3.1 Contexte TI actuel

Rédige 2-3 paragraphes professionnels analysant le profil TI de ${company}.
Base-toi UNIQUEMENT sur les données fournies:
- Secteur: ${responses.industry}
- Taille: ${responses.employees} 
- ${responses.sites}
- Infrastructure: ${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : ''}
- Ressources: ${responses.it_team}
- Maturité transformation: ${responses.digital_transformation_maturity || 'Non évaluée'}
- Positionnement vs compétiteurs: ${responses.competitive_positioning || 'Non évalué'}

RÈGLES CRITIQUES:
- ✅ Utilise UNIQUEMENT les données ci-dessus
- ❌ N'INVENTE AUCUNE donnée
- ✅ Si une donnée manque, indique "Non fourni dans le questionnaire"
- ✅ Analyse professionnelle avec standards COBIT/ITIL

GÉNÈRE MAINTENANT CETTE SECTION COMPLÈTE!`
    },
    
    {
      title: "Évaluation COBIT - Gouvernance (EDM) et Stratégie (APO 1-7)",
      prompt: `GÉNÈRE LA SECTION 2/8 du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2500 mots):

## 4. ÉVALUATION PAR DOMAINE COBIT 2019

### 4.1 EDM - ÉVALUER, DIRIGER ET SURVEILLER (Gouvernance)

**Score EDM: X.X/5.0 [ÉMOJI]**

#### EDM01 - Cadre de gouvernance
- **Comité TI actuel:** ${responses.it_committee}
- **Alignement stratégique:** ${responses.strategic_alignment}
- **Évalue la maturité (1-5)** basée sur COBIT 2019 et ces données réelles
- **Analyse (2 paragraphes):** Compare la situation actuelle aux bonnes pratiques COBIT
- **Écarts identifiés:** Liste les gaps concrets vs standards COBIT
- **Recommandations:** Actions spécifiques basées sur ${responses.it_strategic_priorities || 'les priorités identifiées'}

#### EDM02 - Valeur d'affaires
- **Budget TI:** ${responses.annual_it_budget_exact || responses.annual_it_budget}
- **Alignement affaires-TI:** ${responses.strategic_alignment}
- **Objectifs affaires:** ${responses.business_objectives_detail || 'Non fournis'}
- **Évalue maturité** basée sur ces données réelles
- **Analyse:** Lien entre investissements TI et objectifs d'affaires

Continuer pour EDM03 (Risques), EDM04 (Ressources), EDM05 (Parties prenantes) en analysant les données du questionnaire

### 4.2 APO - ALIGNER, PLANIFIER ET ORGANISER

**Score APO: X.X/5.0 [ÉMOJI]**

#### APO01 - Cadre de gestion
- **Plan directeur:** ${responses.it_roadmap}
- **Maturité:** X/5 [ÉMOJI]
- **Analyse:** [Détails]

#### APO02 - Stratégie
[Analyser]

#### APO03 - Architecture d'entreprise
[Analyser]

#### APO04 - Innovation
[Analyser]

#### APO05 - Portfolio
[Analyser]

#### APO06 - Budget
- **Gestion budgétaire:** ${responses.budget_management}
- **Maturité:** X/5 [ÉMOJI]
[Analyser]

#### APO07 - Ressources humaines
- **Équipe actuelle:** ${responses.it_team}
[Analyser]

**Tableau de synthèse:**
| Processus | Actuel | Cible | Écart | Priorité |
|-----------|--------|-------|-------|----------|
| EDM01 | X/5 | Y/5 | Z | 🔴 |
[Compléter tableau]

GÉNÈRE CETTE SECTION TECHNIQUE ET DÉTAILLÉE!`
    },

    {
      title: "Évaluation COBIT - Projets (BAI), Services (DSS) et Surveillance (MEA)",
      prompt: `GÉNÈRE LA SECTION 3/8 du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2500 mots):

### 4.3 BAI - CONSTRUIRE, ACQUÉRIR ET IMPLANTER

**Score BAI: X.X/5.0 [ÉMOJI]**

#### BAI01 - Gestion des programmes
#### BAI02 - Gestion des exigences
#### BAI03 - Gestion des solutions
#### BAI04 - Disponibilité et capacité
#### BAI05 - Changements organisationnels
#### BAI06 - Gestion des changements TI
- **Processus actuel:** ${responses.change_management}
- **Maturité:** X/5 [ÉMOJI]
[Analyser en détail]
#### BAI07-BAI11
[Analyser tous les processus BAI]

### 4.4 DSS - LIVRER, SERVIR ET SUPPORTER (ITIL 4)

**Score DSS: X.X/5.0 [ÉMOJI]**

#### DSS01 - Gestion des opérations
#### DSS02 - Demandes de service et incidents
- **Gestion incidents:** ${responses.incident_management}
- **Maturité:** X/5 [ÉMOJI]
- **Conformité ITIL 4:** X%
[Analyser]

#### DSS03 - Gestion des problèmes
#### DSS04 - Gestion de la continuité
- **Backup actuel:** ${responses.backup_recovery}
[Analyser en profondeur]

#### DSS05 - Services de sécurité
#### DSS06 - Contrôles des processus métier

### 4.5 MEA - SURVEILLER, ÉVALUER ET APPRÉCIER

**Score MEA: X.X/5.0 [ÉMOJI]**

#### MEA01 - Performance et conformité
- **KPIs actuels:** ${responses.performance_tracking}
- **Maturité:** X/5 [ÉMOJI]
[Analyser]

#### MEA02 - Contrôle interne
- **Documentation:** ${responses.documentation_level}
[Analyser]

#### MEA03 - Conformité externe

**Synthèse scores COBIT:**
| Domaine | Score | Maturité | Priorité |
|---------|-------|----------|----------|
| EDM | X/5 | [ÉMOJI] | 🔴 |
| APO | X/5 | [ÉMOJI] | 🟠 |
| BAI | X/5 | [ÉMOJI] | 🟡 |
| DSS | X/5 | [ÉMOJI] | 🟠 |
| MEA | X/5 | [ÉMOJI] | 🟡 |

GÉNÈRE CETTE SECTION COMPLÈTE!`
    },

    {
      title: "Infrastructure, Benchmarking et Analyse comparative",
      prompt: `GÉNÈRE LA SECTION 4/8 du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2000 mots):

## 5. INFRASTRUCTURE & ARCHITECTURE TECHNIQUE

### 5.1 Architecture actuelle
- **Infrastructure:** ${responses.infrastructure_type}
- **Solutions principales:** ${responses.main_solutions}

### 5.2 Évaluation de la maturité infrastructure
[Analyser: disponibilité, performance, monitoring, scalabilité]

### 5.3 Diagramme d'architecture (ASCII)
\`\`\`
[Créer un diagramme simple mais informatif]
\`\`\`

## 6. ANALYSE COMPARATIVE (BENCHMARKING)

### 6.1 Positionnement sectoriel
Secteur: ${responses.industry}
Taille: ${responses.employees}

| Domaine | ${company} | Moyenne industrie | Leaders | Écart |
|---------|------------|-------------------|---------|-------|
| Gouvernance | X/5 | 3.2/5 | 4.5/5 | -Y |
| Stratégie | X/5 | 3.0/5 | 4.2/5 | -Y |
| Infrastructure | X/5 | 3.5/5 | 4.8/5 | -Y |
| Services | X/5 | 3.1/5 | 4.3/5 | -Y |
| Sécurité | X/5 | 2.8/5 | 4.6/5 | -Y |
| Documentation | X/5 | 2.5/5 | 4.0/5 | -Y |

### 6.2 Analyse des écarts
[Analyser chaque écart et expliquer pourquoi]

### 6.3 Benchmark budgétaire
Budget TI actuel: ${responses.annual_it_budget}
Budget recommandé pour taille similaire: [Calculer]
Écart: [Analyser]

GÉNÈRE CETTE SECTION AVEC DES BENCHMARKS RÉALISTES!`
    },

    {
      title: "Registre des risques et analyse d'impact",
      prompt: `GÉNÈRE LA SECTION 5/8 du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2000 mots):

## 7. REGISTRE DES RISQUES

### 7.1 Matrice de risque globale

\`\`\`
    PROBABILITÉ
      ^
  É   |  🟡    🟠    🔴
  L   |  🟢    🟡    🟠  
  E   |  🟢    🟢    🟡
  V   |
  É   +-------------------->
      FAIBLE MOYEN CRITIQUE
           IMPACT
\`\`\`

### 7.2 Risques identifiés (15-20 risques)

| # | Risque | Prob | Impact | Niveau | Coût potentiel | Mitigation |
|---|--------|------|--------|--------|----------------|------------|
| 1 | Obsolescence infrastructure | Élevé | Critique | 🔴 | XXX,XXX$ | [Actions] |
| 2 | Absence de comité TI formel | Moyen | Élevé | 🟠 | XX,XXX$ | [Actions] |
| 3 | Documentation insuffisante | Élevé | Moyen | 🟡 | XX,XXX$ | [Actions] |
[Générer 15-20 risques réalistes basés sur les réponses]

### 7.3 Analyse d'impact

#### Risque opérationnel
[Analyser impact sur opérations quotidiennes]

#### Risque financier
[Chiffrer pertes potentielles]

#### Risque réputationnel
[Analyser impact sur image/clients]

#### Risque réglementaire
[Analyser conformité et pénalités]

### 7.4 Scénarios catastrophes

**Scénario 1: Panne infrastructure majeure**
- Probabilité: X%
- Impact financier: XXX,XXX$ - X,XXX,XXX$
- Durée d'arrêt: X jours
- Mitigation: [Actions]

**Scénario 2: Perte de données**
[Analyser]

**Scénario 3: Départ clé TI**
[Analyser]

GÉNÈRE CETTE SECTION POUR CRÉER UN SENS D'URGENCE!`
    },

    {
      title: "Recommandations détaillées et priorisées",
      prompt: `GÉNÈRE LA SECTION 6/8 du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2500 mots):

## 8. RECOMMANDATIONS PRIORISÉES (25-30 recommandations)

### 8.1 Recommandations CRITIQUES (0-3 mois) 🔴

**REC-001: [Titre basé sur analyse]**
- **Domaine COBIT:** EDM01
- **Problématique:** [Description du problème identifié]
- **Impact si inaction:** XXX,XXX$ de pertes potentielles annuelles
- **Solution recommandée:** [Détails techniques et organisationnels]
- **Coût estimé:** XX,XXX$ - XX,XXX$
- **Effort:** X jours/homme
- **ROI attendu:** XXX% dans les 12 mois
- **Responsable:** [Rôle suggéré]
- **Échéancier détaillé:**
  - Semaine 1-2: [Phase 1]
  - Semaine 3-4: [Phase 2]
  - Mois 2-3: [Phase 3]
- **Dépendances:** [Si applicable]
- **Critères de succès:** [Mesurables]

Génère 5-8 recommandations CRITIQUES basées UNIQUEMENT sur:
- Pain points: ${responses.main_pain_points || 'Non fournis'}
- Priorités: ${responses.it_strategic_priorities || 'Non fournies'}
- Budget: ${responses.available_budget_initiatives || 'Non défini'}
- Échéancier: ${responses.timeline_expectations || 'Non spécifié'}
- Faiblesses: Sécurité ${responses.security_posture}, Backup ${responses.backup_recovery}

❌ N'INVENTE PAS de recommandations génériques
✅ BASE-TOI sur les données réelles ci-dessus

### 8.2 Recommandations ÉLEVÉES (3-6 mois) 🟠

Génère 6-8 recommandations ÉLEVÉES basées sur les données réelles du questionnaire.
Analyse les domaines: Infrastructure (${responses.infrastructure_age}), Gouvernance (${responses.it_committee}), Performance (${responses.performance_tracking})

### 8.3 Recommandations MOYENNES (6-12 mois) 🟡

Génère 8-10 recommandations MOYENNES basées sur:
- Projets planifiés: ${responses.planned_projects_12months || 'Non fournis'}
- Maturité digitale: ${responses.digital_transformation_maturity || 'Non évaluée'}
- Objectifs affaires: ${responses.business_objectives_detail || 'Non fournis'}

### 8.4 Recommandations BASSES (12-24 mois) 🟢

Génère 5-8 recommandations BASSES (optimisations long terme) basées sur vision stratégique et maturité cible

### 8.5 Synthèse des investissements

| Priorité | Nb Recs | Investissement total | ROI moyen | Délai |
|----------|---------|----------------------|-----------|-------|
| 🔴 CRITIQUE | X | XXX,XXX$ | XXX% | 0-3 mois |
| 🟠 ÉLEVÉE | X | XXX,XXX$ | XXX% | 3-6 mois |
| 🟡 MOYENNE | X | XXX,XXX$ | XXX% | 6-12 mois |
| 🟢 BASSE | X | XX,XXX$ | XXX% | 12-24 mois |
| **TOTAL** | **XX** | **XXX,XXX$** | **XXX%** | **24 mois** |

GÉNÈRE DES RECOMMANDATIONS ACTIONNABLES ET CHIFFRÉES!`
    },

    {
      title: "Roadmap stratégique et budget pluriannuel",
      prompt: `GÉNÈRE LA SECTION 7/8 du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2000 mots):

## 9. FEUILLE DE ROUTE STRATÉGIQUE (3 ANS)

### 9.1 Vision et objectifs

**Vision 2025-2027:**
Base la vision cible sur:
- Objectifs affaires: ${responses.business_objectives_detail || 'Non fournis'}
- Priorités stratégiques: ${responses.it_strategic_priorities || 'Non fournies'}
- Critères succès: ${responses.success_criteria || 'Non fournis'}
- Échéancier: ${responses.timeline_expectations || 'Non spécifié'}

**Objectifs mesurables:**
Définis des objectifs BASÉS sur le contexte de ${company} (${responses.industry}):
- Score maturité global: De [score actuel calculé] → [cible réaliste]
- Conformité standards: De X% → Y% (réaliste selon ressources ${responses.it_team})
- Disponibilité services: Cible basée sur ${responses.backup_recovery}
- KPIs spécifiques à ${responses.industry}

### 9.2 Diagramme Gantt (3 ans)

\`\`\`
ANNÉE 1 (2025)
Construit la roadmap (Gantt ASCII) basée sur:
- Recommandations générées (critiques → moyennes → basses)
- Projets en cours: ${responses.projects_in_progress || 'Aucun'}
- Projets planifiés: ${responses.planned_projects_12months || 'Aucun'}
- Budget disponible: ${responses.available_budget_initiatives || 'Non défini'}
- Échéancier: ${responses.timeline_expectations || 'Non spécifié'}

Q1-Q4: Phases logiques basées sur priorités RÉELLES des recommandations
(Ne pas inventer des phases génériques - base sur les données du questionnaire)
\`\`\`

### 9.3 Jalons et livrables par phase

Détaille les jalons par trimestre basés sur:
- Recommandations prioritaires générées
- Projets existants: ${responses.projects_in_progress || 'Aucun'}
- Budget réel: ${responses.annual_it_budget_exact || responses.annual_it_budget}
- Ressources: ${responses.it_team}

❌ N'invente PAS de jalons génériques
✅ Base sur les recommandations et données réelles

### 9.4 Budgets détaillés

| Phase | Période | CAPEX | OPEX | Total | ROI cumulatif |
|-------|---------|-------|------|-------|---------------|
| Phase 1 | Q1 2025 | XX,XXX$ | XX,XXX$ | XXX,XXX$ | +X% |
Génère phases budgétaires RÉALISTES basées sur:
- Budget annuel: ${responses.annual_it_budget_exact || responses.annual_it_budget}
- Répartition: ${responses.capex_opex_ratio || 'Non suivie'}
- Budget initiatives: ${responses.available_budget_initiatives || 'Non défini'}
- Coûts recommandations (calculés dans section 8)

**Total sur 3 ans:**
Calcule basé sur contraintes budgétaires réelles de ${company} (${responses.industry})
- CAPEX/OPEX: Cohérent avec ${responses.it_budget_breakdown || 'répartition actuelle'}
- ROI: Réaliste pour le secteur ${responses.industry}

## 10. ANALYSE COÛT-BÉNÉFICE

### 10.1 Investissement requis
[Détailler par catégorie]

### 10.2 Bénéfices quantitatifs
- Réduction coûts opérationnels: XXX,XXX$ / an
- Augmentation productivité: XX%
- Réduction incidents: XX%
- [Autres bénéfices chiffrés]

### 10.3 Bénéfices qualitatifs
- Amélioration satisfaction utilisateurs
- Agilité organisationnelle accrue
- Positionnement compétitif
- [Autres bénéfices]

GÉNÈRE CETTE ROADMAP RÉALISTE ET BUDGÉTÉE!`
    },

    {
      title: "Conclusion et Annexes techniques",
      prompt: `GÉNÈRE LA SECTION 8/8 (FINALE) du Bilan de Santé TI pour: ${company}

CONTEXTE: ${context}

CONTENU (2500 mots):

## 11. CONCLUSION & PROCHAINES ÉTAPES

### 11.1 Synthèse de l'état actuel
[Résumer en 3-4 paragraphes les constats majeurs]

### 11.2 Vision cible recommandée
[Décrire l'état souhaité dans 12-24 mois]

### 11.3 Plan d'action immédiat (30 jours)

| Semaine | Actions | Responsable | Budget |
|---------|---------|-------------|--------|
| 1-2 | [Actions] | [Rôle] | XXX$ |
[Plan détaillé 4 semaines]

### 11.4 Engagement BZ
[Message de Dany Bourassa sur l'accompagnement]

---

# ANNEXES TECHNIQUES

## ANNEXE A: GRILLES D'ÉVALUATION COBIT 2019

| Code | Processus | Score | Justification | Preuves observées |
|------|-----------|-------|---------------|-------------------|
| EDM01 | Cadre de gouvernance | X/5 | [Détails] | [Observations] |
| EDM02 | Valeur d'affaires | X/5 | [Détails] | [Observations] |
[Compléter pour les 40 processus COBIT]

## ANNEXE B: GRILLES D'ÉVALUATION ITIL 4

| Pratique ITIL | Score | Conformité % | Écarts identifiés |
|---------------|-------|--------------|-------------------|
| Service Desk | X/5 | XX% | [Détails] |
[Compléter pour les 34 pratiques]

## ANNEXE C: INVENTAIRE DÉTAILLÉ

### C.1 Inventaire des serveurs

**Nombre total de serveurs:** ${responses.server_count || 'Non spécifié'}
**Plateforme de virtualisation:** ${responses.virtualization_platform || 'Non spécifié'}

${responses.server_inventory_text && typeof responses.server_inventory_text === 'string' ? `
| Nom | Rôle | OS / Version | Âge | Spécifications |
|-----|------|--------------|-----|----------------|
${responses.server_inventory_text.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 3) {
    return `| ${parts.join(' | ')} |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '| Nom | Rôle | OS / Version | Âge | Spécifications |\n|-----|------|--------------|-----|----------------|\n| *Aucun inventaire fourni* | - | - | - | - |'}

**Âge moyen infrastructure:** ${responses.infrastructure_age || 'Non spécifié'}
**Cycle de remplacement:** ${responses.replacement_cycle || 'Non défini'}
**Localisation:** ${responses.server_room_location || 'Non spécifiée'}

**Sécurité physique salle serveurs:**
${Array.isArray(responses.server_room_security) ? responses.server_room_security.map((s: string) => `- ✓ ${s}`).join('\n') : '- Aucune information'}

### C.2 Inventaire des équipements réseau

${responses.network_equipment_inventory && typeof responses.network_equipment_inventory === 'string' ? `
| Équipement | Type | Caractéristiques | Âge |
|------------|------|------------------|-----|
${responses.network_equipment_inventory.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 2) {
    return `| ${parts.join(' | ')} |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '| Équipement | Type | Caractéristiques | Âge |\n|------------|------|------------------|-----|\n| *Aucun inventaire fourni* | - | - | - |'}

**Fournisseur Internet:** ${responses.internet_provider_primary || 'Non spécifié'}
**Bande passante:** ${responses.internet_bandwidth_primary || 'Non spécifiée'}
**Redondance:** ${responses.internet_redundancy || 'Non spécifiée'}
**Segmentation VLANs:** ${responses.vlan_segmentation || 'Non spécifiée'}

### C.3 Inventaire des applications critiques

**Plan Microsoft 365:** ${responses.microsoft365_plan || 'Non utilisé'}
**Solution ERP:** ${responses.erp_solution || 'Non applicable'}
**Solution CRM:** ${responses.crm_solution || 'Non applicable'}
**Solution comptable:** ${responses.accounting_solution || 'Non spécifiée'}

${responses.critical_apps_inventory && typeof responses.critical_apps_inventory === 'string' ? `
| # | Application | Éditeur | Utilisateurs | Criticité |
|---|-------------|---------|--------------|-----------|
${responses.critical_apps_inventory.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 2) {
    return `| ${parts.join(' | ')} |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '| # | Application | Éditeur | Utilisateurs | Criticité |\n|---|-------------|---------|--------------|-----------||\n| *Aucun inventaire fourni* | - | - | - | - |'}

**Niveau d'intégration:** ${responses.app_integration || 'Non évalué'}
**Répartition Cloud/On-Premise:** ${responses.cloud_vs_onprem_ratio || 'Non spécifiée'}
**Coût annuel licences:** ${responses.annual_licensing_cost || 'Non connu'}

### C.4 Équipe TI

**Nombre de personnes TI internes:** ${responses.it_team_count || '0'}
**Niveau du responsable:** ${responses.it_manager_level || 'Non spécifié'}
**Fournisseur externe:** ${responses.external_it_provider || 'Aucun'}
**Type de support externe:** ${responses.external_it_support_type || 'Aucun'}

${responses.it_team_roles && typeof responses.it_team_roles === 'string' ? `
**Rôles et responsabilités:**
${responses.it_team_roles.split('\n').map((line: string) => `- ${line}`).join('\n')}
` : '*Aucune information sur les rôles*'}

**Lacunes de compétences identifiées:**
${Array.isArray(responses.it_skills_gaps) ? responses.it_skills_gaps.map((s: string) => `- ${s}`).join('\n') : '- Aucune lacune identifiée'}

**Budget formation annuel:** ${responses.it_training_budget || 'Non défini'}

### C.5 Contrats majeurs

**Budget TI annuel total:** ${responses.annual_it_budget_exact || responses.annual_it_budget || 'Non défini'}
**Répartition CAPEX/OPEX:** ${responses.capex_opex_ratio || 'Non suivie'}

${responses.major_contracts && typeof responses.major_contracts === 'string' ? `
| Fournisseur | Objet | Échéance | Coût annuel |
|-------------|-------|----------|-------------|
${responses.major_contracts.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 3) {
    return `| ${parts.join(' | ')} |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '| Fournisseur | Objet | Échéance | Coût annuel |\n|-------------|-------|----------|-------------|\n| *Aucun contrat documenté* | - | - | - |'}

${responses.it_budget_breakdown ? `
**Répartition budgétaire détaillée:**
\`\`\`
${responses.it_budget_breakdown}
\`\`\`
` : ''}

### C.6 Projets en cours et planifiés

${responses.projects_in_progress && typeof responses.projects_in_progress === 'string' ? `
**Projets en cours:**
${responses.projects_in_progress.split('\n').map((line: string) => `- ${line}`).join('\n')}
` : '*Aucun projet en cours*'}

${responses.planned_projects_12months && typeof responses.planned_projects_12months === 'string' ? `
**Projets planifiés (12 prochains mois):**
${responses.planned_projects_12months.split('\n').map((line: string) => `- ${line}`).join('\n')}
` : '*Aucun projet planifié*'}

**Budget disponible pour initiatives:** ${responses.available_budget_initiatives || 'Non défini'}

## ANNEXE D: GLOSSAIRE TECHNIQUE

Génère un glossaire de 30-50 termes PERTINENTS AU CONTEXTE DE ${company}.

Base la sélection des termes sur:
- Technologies mentionnées: ${Array.isArray(responses.main_solutions) ? responses.main_solutions.join(', ') : ''}
- Infrastructure: ${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : ''}
- Solutions cloud: ${responses.microsoft365_plan}, ${responses.erp_solution}, ${responses.crm_solution}
- Sécurité: ${responses.security_posture}, ${responses.identity_access}
- Standards: COBIT 2019, ITIL 4, ISO 20000, CMMi

Exemple format:
**Active Directory (AD):** Service d'annuaire Microsoft pour la gestion centralisée des identités et des accès.
**COBIT:** Control Objectives for Information and Related Technologies - Cadre de gouvernance IT.

❌ N'inclus PAS de termes non pertinents au contexte de ${company}
✅ Priorise les termes techniques utilisés dans le document

## ANNEXE E: RÉFÉRENCES & BIBLIOGRAPHIE

Inclus 10-15 références professionnelles PERTINENTES:

**Standards et cadres normatifs:**
- ISACA. (2019). COBIT 2019 Framework: Governance and Management Objectives.
- AXELOS. (2019). ITIL Foundation, ITIL 4 Edition.
- ISO/IEC. (2018). ISO/IEC 20000-1:2018 Information technology — Service management.
- CMMi Institute. (2021). CMMi v2.0 Model.

**Publications spécifiques au secteur ${responses.industry}:**
Ajoute 3-5 références pertinentes pour l'industrie ${responses.industry}

**Meilleures pratiques et recherche:**
Ajoute 3-5 références de Gartner, Forrester, IDC pertinentes au contexte

## ANNEXE F: CONTACTS BZ

**Dany Bourassa**
Expert TI Senior
Bergeron Zaccheo (BZ) inc.
Téléphone: 418-688-2727
Courriel: dany@bzinc.ca
Web: www.bzinc.ca

---

**FIN DU DOCUMENT - ${company}**

GÉNÈRE CETTE SECTION FINALE COMPLÈTE ET PROFESSIONNELLE!`
    }
  ];
}

// 🛡️ Définition des sections pour l'Audit de Sécurité
function getAuditSecuriteSections(context: string, responses: Record<string, string | string[]>) {
  const company = responses.company_name || 'Votre organisation';
  const date = new Date().toLocaleDateString('fr-FR');
  
  return [
    {
      title: "Page titre, méthodologie et sommaire exécutif sécurité",
      prompt: `Tu es un expert cybersécurité chez BZ, certifié CISSP, CISM, CEH, spécialisé NIST CSF 2.0, ISO 27001:2022, CIS v8.

GÉNÈRE LA SECTION 1/8 de l'Audit de Sécurité pour: ${company}
CONTEXTE: ${context}

CONTENU (2500 mots):

# AUDIT DE SÉCURITÉ - ${company}
Date: ${date}
Classification: **CONFIDENTIEL**
Consultant: Dany Bourassa, Expert Cybersécurité

## 1. SOMMAIRE EXÉCUTIF

### 1.1 Score de sécurité global
\`\`\`
╔═══════════════════════════════════╗
║  SCORE DE SÉCURITÉ GLOBAL        ║
║     [CALCULER] / 100             ║
║  Niveau de risque: [ÉMOJI]       ║
╚═══════════════════════════════════╝
\`\`\`

### 1.2 Spider Chart NIST CSF 2.0

### 1.3 Top 10 vulnérabilités critiques
[Liste avec CVSS si applicable]

### 1.4 Actions immédiates (0-30 jours)
[5-8 actions urgentes]

Posture actuelle: ${responses.security_posture}
IAM: ${responses.identity_access}
Protection données: ${responses.data_protection}
Détection: ${responses.threat_detection}

GÉNÈRE SECTION ALARMANTE SI NÉCESSAIRE!`
    },
    {
      title: "Évaluation NIST CSF 2.0 - GOVERN et IDENTIFY",
      prompt: `SECTION 2/8 Audit Sécurité - ${company}
CONTEXTE: ${context}

## NIST CSF 2.0 ÉVALUATION

### GOVERN (GV) - Score: X%
[Analyse gouvernance sécurité]

### IDENTIFY (ID) - Score: X%
[Analyse identification actifs/risques]

GÉNÈRE 2500 mots détaillés!`
    },
    {
      title: "Évaluation NIST CSF 2.0 - PROTECT",
      prompt: `SECTION 3/8 - ${company}
CONTEXTE: ${context}

### PROTECT (PR) - Score: X%

#### PR.AA - IAM: ${responses.identity_access}
#### PR.DS - Protection données: ${responses.data_protection}
#### PR.AT - Formation: ${responses.security_training}

GÉNÈRE analyse PROTECT complète (2500 mots)!`
    },
    {
      title: "Évaluation NIST CSF 2.0 - DETECT, RESPOND, RECOVER",
      prompt: `SECTION 4/8 - ${company}
CONTEXTE: ${context}

### DETECT (DE) - Score: X%
Détection: ${responses.threat_detection}

### RESPOND (RS) - Score: X%
Plan réponse: ${responses.incident_response}

### RECOVER (RC) - Score: X%
Backup: ${responses.backup_recovery}

GÉNÈRE 2500 mots!`
    },
    {
      title: "ISO 27001:2022 - Conformité des 93 contrôles",
      prompt: `SECTION 5/8 - ${company}
CONTEXTE: ${context}

## ISO 27001:2022 (Annexe A - 93 contrôles)

### A.5 - Contrôles organisationnels (37)
### A.6 - Contrôles de personnel (8)
### A.7 - Contrôles physiques (14)
### A.8 - Contrôles technologiques (34)

Vulnérabilités: ${responses.vulnerabilities}

Tableau de conformité pour TOUS les 93 contrôles!
GÉNÈRE 2500 mots!`
    },
    {
      title: "Registre des risques cyber et scénarios d'attaque",
      prompt: `SECTION 6/8 - ${company}
CONTEXTE: ${context}

## REGISTRE DES RISQUES CYBER (25-30 risques)

Matrice de risque + détails pour chaque risque

## SCÉNARIOS D'ATTAQUE

### Scénario Ransomware
[Détails Kill Chain]

### Scénario Phishing
### Scénario APT

GÉNÈRE 2000 mots alarmants!`
    },
    {
      title: "Plan d'action sécurité priorisé (30-40 actions)",
      prompt: `SECTION 7/8 - ${company}
CONTEXTE: ${context}

## PLAN D'ACTION SÉCURITÉ (30-40 recommandations)

### Actions CRITIQUES 🔴 (0-30 jours)
[10-12 actions avec $, effort, ROI]

### Actions ÉLEVÉES 🟠 (1-3 mois)
[8-10 actions]

### Actions MOYENNES 🟡 (3-6 mois)
[10-12 actions]

### Actions BASSES 🟢 (6-12 mois)
[8-10 actions]

Conformité: ${responses.compliance}

GÉNÈRE 2500 mots actionnables!`
    },
    {
      title: "Roadmap sécurité, conformité réglementaire et annexes",
      prompt: `SECTION 8/8 FINALE - ${company}
CONTEXTE: ${context}

## ROADMAP SÉCURITÉ (3 ANS)

Diagramme Gantt + budgets

## CONFORMITÉ RÉGLEMENTAIRE

${responses.compliance} - Analyse article par article

## ANNEXES

## ANNEXE A: GRILLES D'ÉVALUATION COMPLÈTES

### A.1 Grille NIST CSF 2.0 (93 sous-catégories)
[IA complétera avec évaluation détaillée]

### A.2 Grille ISO 27001:2022 (93 contrôles)
[IA complétera avec statut de conformité]

### A.3 Grille CIS Controls v8 (18 contrôles)
[IA complétera avec scores]

## ANNEXE B: INVENTAIRE SÉCURITÉ DÉTAILLÉ

### B.1 Infrastructure sécurisée

**Nombre total de serveurs:** ${responses.server_count || 'Non spécifié'}
**Plateforme de virtualisation:** ${responses.virtualization_platform || 'Non spécifié'}

${responses.server_inventory_text && typeof responses.server_inventory_text === 'string' ? `
**Inventaire des serveurs:**
| Nom | Rôle | OS / Version | Âge | Risque EOL |
|-----|------|--------------|-----|------------|
${responses.server_inventory_text.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 3) {
    return `| ${parts.join(' | ')} |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '*Aucun inventaire serveur fourni*'}

**Sécurité physique:**
${Array.isArray(responses.server_room_security) ? responses.server_room_security.map((s: string) => `- ✓ ${s}`).join('\n') : '- Aucune information'}

### B.2 Équipements réseau et sécurité périmétrique

${responses.network_equipment_inventory && typeof responses.network_equipment_inventory === 'string' ? `
| Équipement | Type | Version firmware | Âge | Vulnérabilités connues |
|------------|------|------------------|-----|------------------------|
${responses.network_equipment_inventory.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 2) {
    return `| ${parts.join(' | ')} | À vérifier | À auditer |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '*Aucun inventaire réseau fourni*'}

**Configuration réseau:**
- Fournisseur Internet: ${responses.internet_provider_primary || 'Non spécifié'}
- Bande passante: ${responses.internet_bandwidth_primary || 'Non spécifiée'}
- Redondance: ${responses.internet_redundancy || 'Non spécifiée'}
- Segmentation VLANs: ${responses.vlan_segmentation || 'Non spécifiée'}
- Solution VPN: ${responses.vpn_solution || 'Non spécifiée'}
- Utilisateurs VPN: ${responses.vpn_users_count || 'Non spécifié'}

**Infrastructure Wi-Fi:**
${Array.isArray(responses.wifi_infrastructure) ? responses.wifi_infrastructure.map((s: string) => `- ${s}`).join('\n') : '- Aucune information'}

### B.3 Applications critiques et surface d'attaque

${responses.critical_apps_inventory && typeof responses.critical_apps_inventory === 'string' ? `
| Application | Éditeur | Utilisateurs | Criticité | Exposition Internet | Données sensibles |
|-------------|---------|--------------|-----------|---------------------|-------------------|
${responses.critical_apps_inventory.split('\n').map((line: string) => {
  const parts = line.split('|').map((p: string) => p.trim());
  if (parts.length >= 2) {
    return `| ${parts.join(' | ')} | À évaluer | À classifier |`;
  }
  return null;
}).filter(Boolean).join('\n')}
` : '*Aucun inventaire applicatif fourni*'}

**Applications cloud:**
- Microsoft 365: ${responses.microsoft365_plan || 'Non utilisé'}
- ERP: ${responses.erp_solution || 'Non applicable'}
- CRM: ${responses.crm_solution || 'Non applicable'}
- Cloud/On-Premise: ${responses.cloud_vs_onprem_ratio || 'Non spécifié'}

### B.4 Contrôles de sécurité en place

**Posture globale:** ${responses.security_posture || 'Non évaluée'}

**Identité et accès (IAM):**
- Niveau actuel: ${responses.identity_access || 'Non spécifié'}

**Protection des données:**
- Niveau actuel: ${responses.data_protection || 'Non spécifié'}

**Détection de menaces:**
- Niveau actuel: ${responses.threat_detection || 'Non spécifié'}

**Réponse aux incidents:**
- Niveau actuel: ${responses.incident_response || 'Non spécifié'}

**Sauvegarde et reprise:**
- Niveau actuel: ${responses.backup_recovery || 'Non spécifié'}

**Conformité réglementaire:**
${Array.isArray(responses.compliance) ? responses.compliance.map((c: string) => `- ${c}`).join('\n') : '- Aucune exigence identifiée'}

**Formation sécurité:**
- Programme actuel: ${responses.security_training || 'Non spécifié'}

**Gestion des vulnérabilités:**
- Processus actuel: ${responses.vulnerabilities || 'Non spécifié'}

### B.5 Équipe de sécurité

**Personnel TI total:** ${responses.it_team_count || '0'} personne(s)

${responses.it_team_roles && typeof responses.it_team_roles === 'string' ? `
**Rôles et responsabilités:**
${responses.it_team_roles.split('\n').map((line: string) => `- ${line}`).join('\n')}
` : '*Aucune information sur l\'équipe*'}

**Support externe:** ${responses.external_it_provider || 'Aucun'}

**Lacunes compétences sécurité:**
${Array.isArray(responses.it_skills_gaps) ? responses.it_skills_gaps.filter((s: string) => s.includes('Cybersécurité') || s.includes('Sécurité')).map((s: string) => `- ⚠️ ${s}`).join('\n') : '- Non évaluées'}

## ANNEXE C: PLAYBOOKS RÉPONSE AUX INCIDENTS

Génère 5 playbooks ADAPTÉS AU CONTEXTE de ${company}:

Base les playbooks sur:
- Plan réponse incidents actuel: ${responses.incident_response}
- Équipe TI: ${responses.it_team} + ${responses.external_it_provider || 'Aucun support externe'}
- Infrastructure: ${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : ''}
- Solutions sécurité: ${responses.threat_detection}, ${responses.data_protection}
- Backup: ${responses.backup_recovery}

Pour CHAQUE playbook (C.1 à C.5), inclus:
1. **Détection:** Comment l'incident est détecté (basé sur ${responses.threat_detection})
2. **Confinement:** Étapes immédiates (basé sur ressources ${responses.it_team})
3. **Éradication:** Actions correctives
4. **Récupération:** Procédures de restauration (basé sur ${responses.backup_recovery})
5. **Leçons apprises:** Amélioration continue

❌ N'invente PAS de capacités non existantes chez ${company}
✅ Adapte aux ressources réelles disponibles

## ANNEXE D: GLOSSAIRE CYBERSÉCURITÉ

**APT (Advanced Persistent Threat):** Menace persistante avancée - attaque sophistiquée et ciblée.

**EDR (Endpoint Detection & Response):** Solution de détection et réponse sur les postes de travail.

**MFA (Multi-Factor Authentication):** Authentification multi-facteurs.

**NIST CSF:** NIST Cybersecurity Framework - cadre de gestion des risques cyber.

**PAM (Privileged Access Management):** Gestion des accès privilégiés.

**SIEM (Security Information and Event Management):** Gestion centralisée des événements de sécurité.

**SOC (Security Operations Center):** Centre opérationnel de sécurité.

**Zero Trust:** Modèle de sécurité "ne jamais faire confiance, toujours vérifier".

Ajoute 30-40 termes cybersécurité PERTINENTS AU CONTEXTE de ${company}:

Base la sélection sur:
- Technologies sécurité: ${responses.threat_detection}, ${responses.identity_access}, ${responses.data_protection}
- Solutions cloud: ${responses.microsoft365_plan}, ${responses.cloud_vs_onprem_ratio}
- Standards: NIST CSF 2.0, ISO 27001:2022, CIS Controls v8
- Conformité: ${Array.isArray(responses.compliance) ? responses.compliance.join(', ') : ''}

❌ N'inclus PAS de termes non utilisés dans le document
✅ Priorise les acronymes et concepts mentionnés dans l'audit

## ANNEXE E: CONTACTS BZ

**Dany Bourassa**
Expert Cybersécurité Senior
Bergeron Zaccheo (BZ) inc.
Téléphone: 418-688-2727
Courriel: dany@bzinc.ca
Web: www.bzinc.ca

---

**FIN AUDIT SÉCURITÉ - ${company}**
**Document confidentiel - Distribution restreinte**

GÉNÈRE CETTE SECTION FINALE COMPLÈTE ET PROFESSIONNELLE!`
    }
  ];
}

// ⚠️ ANCIENNES FONCTIONS DE GÉNÉRATION - NE SONT PLUS UTILISÉES
// Les documents sont maintenant générés par sections via generateBilanSanteTIBySection et generateAuditSecuriteBySection

/* 
// ANCIENNE VERSION - CONSERVÉE POUR RÉFÉRENCE
async function generateBilanSanteTI(
  context: string,
  responses: Record<string, string | string[]>
): Promise<string> {
  const prompt = `Tu es LE MEILLEUR consultant TI senior au monde chez Bergeron Zaccheo (BZ), expert certifié en COBIT 2019, ITIL 4, ISO/IEC 20000-1:2018 et CMMi niveau 5.

**MISSION ULTRA-CRITIQUE:** Génère un **BILAN DE SANTÉ TI** de calibre Fortune 500, document MASSIF de 50+ pages, ultra-professionnel, normé et IMPRESSIONNANT pour le client suivant.

---

## 📋 INFORMATIONS CLIENT
${context}

---

## 🎯 CADRE NORMATIF

**Livrable BZ:** Bilan de santé TI  
**Document normé équivalent:** Rapport de maturité en gouvernance et gestion des services TI selon COBIT 2019 + ITIL 4 + ISO/IEC 20000

Ce document doit être aligné avec les standards internationaux de gouvernance TI et démontrer une rigueur professionnelle absolue.

---

## 📄 STRUCTURE OBLIGATOIRE DU DOCUMENT (50+ PAGES)

Le document DOIT être divisé en 2 PARTIES DISTINCTES:

---

# 📋 PARTIE I: RÉSUMÉ EXÉCUTIF & ANALYSE (15-20 pages)
*(Cette partie sera présentée au client - digestible, impactante, visuelle)*

---

## **SECTION 1: PAGE TITRE**
- Logo BZ / En-tête professionnel
- Titre: "BILAN DE SANTÉ TI"
- Sous-titre: "Évaluation de la maturité TI et analyse des écarts"
- Client: **${responses.company_name}**
- Date: **${new Date().toLocaleDateString('fr-FR')}**
- Consultant: Dany Bourassa, Expert TI Senior
- Référence: BZ-BST-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}

## **SECTION 2: TABLE DES MATIÈRES COMPLÈTE**
*(Liste toutes les sections avec numéros de page)*

## **SECTION 3: VERSIONNAGE & HISTORIQUE**
| Version | Date | Auteur | Modifications |
|---------|------|--------|---------------|
| 1.0 | ${new Date().toLocaleDateString('fr-FR')} | Dany Bourassa | Version initiale |

## **SECTION 4: MÉTHODOLOGIE D'ÉVALUATION**

### 4.1 Cadres normatifs utilisés
- **COBIT 2019** (Control Objectives for Information and Related Technologies)
  - Domaines EDM, APO, BAI, DSS, MEA
- **ITIL 4** (Information Technology Infrastructure Library)
  - Service Value System (SVS)
  - Pratiques de gestion des services
- **ISO/IEC 20000-1:2018** (Gestion des services)
- **CMMi v2.0** (Capability Maturity Model Integration)

### 4.2 Échelle de maturité (1-5)
Utilise cette échelle avec ÉMOJIS pour CHAQUE évaluation:

| Niveau | Symbole | Description | Capacité |
|--------|---------|-------------|----------|
| 1 | 🔴 | **Initial / Ad-hoc** | Processus chaotiques, imprévisibles, réactifs |
| 2 | 🟠 | **Reproductible** | Processus de base définis, partiellement documentés |
| 3 | 🟡 | **Défini** | Processus standardisés, documentés et communiqués |
| 4 | 🟢 | **Géré** | Processus mesurés, contrôlés et optimisés |
| 5 | 🟦 | **Optimisé** | Amélioration continue, innovation, excellence |

### 4.3 Méthode d'analyse
- Entrevues avec intervenants clés
- Revue documentaire
- Observation terrain
- Analyse d'écarts (Gap Analysis)
- Benchmarking sectoriel

## **SECTION 5: SOMMAIRE EXÉCUTIF (2 pages)**

### 5.1 Score de maturité global

\`\`\`
╔════════════════════════════════════════════════════════════╗
║       SCORE GLOBAL DE MATURITÉ TI                          ║
║                                                            ║
║              ★★★☆☆  (3.2 / 5.0)                          ║
║                                                            ║
║            Niveau: DÉFINI (🟡)                            ║
║     Prochain objectif: GÉRÉ (🟢)                          ║
╚════════════════════════════════════════════════════════════╝
\`\`\`

### 5.2 Spider Chart - Vue d'ensemble de la maturité

\`\`\`
           Gouvernance (2.5/5) 🟠
                    |
        Stratégie  /|\  Performance
         (3.0/5)  / | \    (3.5/5)
           🟡    /  |  \      🟡
                /   |   \
               /    |    \
         Services Infrastructure
          (3.2/5)   |    (3.8/5)
            🟡      |      🟡
                    |
              Documentation
                (2.8/5) 🟠
\`\`\`

### 5.3 Constats principaux

**🟢 FORCES IDENTIFIÉES:**
1. [Insérer force majeure basée sur les réponses]
2. [Insérer force majeure basée sur les réponses]
3. [Insérer force majeure basée sur les réponses]

**🔴 FAIBLESSES CRITIQUES:**
1. [Insérer faiblesse critique basée sur les réponses]
2. [Insérer faiblesse critique basée sur les réponses]
3. [Insérer faiblesse critique basée sur les réponses]

**⚠️ RISQUES MAJEURS SI INACTION:**
- Risque opérationnel: [Décrire impact]
- Risque financier: [Chiffrer pertes potentielles]
- Risque réputationnel: [Décrire conséquences]

### 5.4 Recommandations prioritaires (Top 5)

| # | Recommandation | Priorité | Échéance | Investissement | Impact |
|---|----------------|----------|----------|----------------|--------|
| 1 | [Rec critique] | 🔴 URGENT | 0-3 mois | XX,XXX $ | ÉLEVÉ |
| 2 | [Rec importante] | 🟠 ÉLEVÉE | 3-6 mois | XX,XXX $ | MOYEN |
| 3 | [Rec importante] | 🟠 ÉLEVÉE | 3-6 mois | XX,XXX $ | MOYEN |
| 4 | [Rec moyenne] | 🟡 MOYENNE | 6-12 mois | XX,XXX $ | MOYEN |
| 5 | [Rec moyenne] | 🟡 MOYENNE | 6-12 mois | XX,XXX $ | FAIBLE |

## **SECTION 6: PROFIL ORGANISATIONNEL**

| Attribut | Valeur |
|----------|--------|
| **Organisation** | ${responses.company_name} |
| **Secteur d'activité** | ${responses.industry} |
| **Taille** | ${responses.employees} |
| **Sites** | ${responses.sites} |
| **Budget TI annuel** | ${responses.annual_it_budget} |
| **Ressources TI** | ${responses.it_team} |
| **Infrastructure** | ${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : responses.infrastructure_type} |

### 6.1 Contexte TI actuel
[Générer paragraphe analysant le profil]

### 6.2 Positionnement dans l'industrie
[Comparer avec benchmarks du secteur]

### **4. ÉVALUATION DE MATURITÉ PAR PILIER (COBIT 2019)**

Pour chaque pilier COBIT, évalue la maturité (0-5) et explique:

**Pilier 1: GOUVERNANCE & STRATÉGIE TI**
- Alignement stratégique affaires-TI
- Comité de gouvernance
- Plan directeur / Roadmap
- Gestion budgétaire
- **Score de maturité: X/5**
- **Constat:** (analyse détaillée)
- **Écarts identifiés:** (gap analysis)

**Pilier 2: GESTION DES SERVICES (ITIL 4)**
- Gestion des incidents
- Gestion des changements
- Gestion des problèmes
- Service desk et support
- Continuité de service
- **Score de maturité: X/5**
- **Constat:**
- **Écarts identifiés:**

**Pilier 3: INFRASTRUCTURE & OPÉRATIONS**
- Architecture technique (${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : ''})
- Disponibilité et performance
- Monitoring et surveillance
- Sauvegarde et reprise (${responses.backup_recovery})
- **Score de maturité: X/5**
- **Constat:**
- **Écarts identifiés:**

**Pilier 4: DOCUMENTATION & PROCESSUS**
- Niveau de documentation: ${responses.documentation_level}
- Processus formalisés
- Base de connaissances
- Traçabilité et audit trail
- **Score de maturité: X/5**
- **Constat:**
- **Écarts identifiés:**

**Pilier 5: PERFORMANCE & MESURE**
- KPIs et métriques: ${responses.performance_tracking}
- Tableaux de bord
- Rapports de gestion
- Amélioration continue
- **Score de maturité: X/5**
- **Constat:**
- **Écarts identifiés:**

### **5. ANALYSE COMPARATIVE (BENCHMARKING)**
Comparer la maturité du client avec les standards de l'industrie (${responses.industry}) pour une organisation de taille similaire (${responses.employees}).

Créer un tableau:
| Domaine | Client | Industrie | Écart |
|---------|--------|-----------|-------|
| Gouvernance | X/5 | Y/5 | +/- Z |
| ...

### **6. RISQUES IDENTIFIÉS**
Lister 5-8 risques critiques avec:
- Description du risque
- Impact potentiel (faible/moyen/élevé/critique)
- Probabilité
- Mitigation recommandée

### **7. RECOMMANDATIONS PRIORISÉES**

**Priorité CRITIQUE (0-3 mois):**
1. [Recommandation détaillée avec justification]
2. ...

**Priorité ÉLEVÉE (3-6 mois):**
1. ...

**Priorité MOYENNE (6-12 mois):**
1. ...

### **8. FEUILLE DE ROUTE (Roadmap haute-niveau)**
- Phase 1 (Q1): Stabilisation et fondations
- Phase 2 (Q2): Optimisation processus
- Phase 3 (Q3-Q4): Transformation et excellence

### **9. CONCLUSION**
- Résumé de l'état actuel
- Vision cible recommandée
- Investissement estimé
- Bénéfices attendus (ROI qualitatif)

---

## 📐 EXIGENCES DE QUALITÉ

1. **Utilise un français impeccable et professionnel**
2. **Sois TRÈS spécifique** - utilise les données du contexte client
3. **Justifie chaque score de maturité** avec des exemples concrets
4. **Fournis des recommandations actionnables** (pas de généralités)
5. **Minimum 2500 mots** - ce document doit être complet et crédible
6. **Ton sérieux et expert** - c'est un document auditable

---

**GÉNÈRE MAINTENANT LE BILAN DE SANTÉ TI COMPLET !** 🚀`;

  return await callOpenAI(prompt);
}

// 🛡️ GÉNÉRATION DOCUMENT 2: AUDIT DE SÉCURITÉ
async function generateAuditSecurite(
  context: string,
  responses: Record<string, string | string[]>
): Promise<string> {
  const prompt = `Tu es LE MEILLEUR expert en cybersécurité au monde chez Bergeron Zaccheo (BZ), spécialisé en NIST Cybersecurity Framework 2.0, ISO/IEC 27001:2022 et CIS Controls v8.

**MISSION CRITIQUE:** Génère un **AUDIT DE SÉCURITÉ** ultra-professionnel et normé pour le client suivant.

---

## 📋 INFORMATIONS CLIENT
${context}

---

## 🎯 CADRE NORMATIF

**Livrable BZ:** Audit de sécurité  
**Document normé équivalent:** Analyse des écarts en cybersécurité selon NIST Cybersecurity Framework 2.0 + ISO/IEC 27001:2022 + CIS Controls v8

Ce document doit être aligné avec les standards internationaux de cybersécurité et démontrer une analyse rigoureuse des risques.

---

## 📄 STRUCTURE OBLIGATOIRE DU DOCUMENT

### **1. PAGE TITRE ET MÉTHODOLOGIE**
- Titre: "AUDIT DE SÉCURITÉ"
- Sous-titre: "Analyse des écarts en cybersécurité et évaluation de la posture de sécurité"
- Client: ${responses.company_name}
- Date: ${new Date().toLocaleDateString('fr-FR')}
- Cadres de référence: NIST CSF 2.0, ISO/IEC 27001:2022, CIS Controls v8
- Posture actuelle: ${responses.security_posture}
- Méthodologie d'évaluation

### **2. SOMMAIRE EXÉCUTIF (1 page)**
- Score de sécurité global (0-100)
- Niveau de risque cyber (faible/moyen/élevé/critique)
- Top 5 vulnérabilités critiques
- 3-5 actions immédiates recommandées
- Exposition aux menaces (ransomware, phishing, etc.)

### **3. PÉRIMÈTRE DE L'AUDIT**
- Infrastructure couverte: ${Array.isArray(responses.infrastructure_type) ? responses.infrastructure_type.join(', ') : ''}
- Solutions analysées: ${Array.isArray(responses.main_solutions) ? responses.main_solutions.join(', ') : ''}
- Conformité évaluée: ${Array.isArray(responses.compliance) ? responses.compliance.join(', ') : ''}

### **4. ÉVALUATION SELON NIST CSF 2.0 (5 FONCTIONS)**

**FONCTION 1: IDENTIFY (Identifier)**
Évalue:
- Gestion des actifs
- Environnement d'affaires
- Gouvernance de la sécurité
- Évaluation des risques
- Gestion des risques de la chaîne d'approvisionnement
- **Score: X/100**
- **Conformité: X%**
- **Écarts critiques:** [Liste détaillée]
- **Recommandations:**

**FONCTION 2: PROTECT (Protéger)**
Évalue:
- Gestion des identités et accès: ${responses.identity_access}
- Protection des données: ${responses.data_protection}
- Formation et sensibilisation: ${responses.security_training}
- Sécurité des plateformes
- Résilience technologique
- **Score: X/100**
- **Conformité: X%**
- **Écarts critiques:**
- **Recommandations:**

**FONCTION 3: DETECT (Détecter)**
Évalue:
- Surveillance continue: ${responses.threat_detection}
- Détection d'anomalies
- Événements de sécurité
- Monitoring et alertes
- **Score: X/100**
- **Conformité: X%**
- **Écarts critiques:**
- **Recommandations:**

**FONCTION 4: RESPOND (Répondre)**
Évalue:
- Plan de réponse aux incidents: ${responses.incident_response}
- Communications
- Analyse post-incident
- Mitigation des impacts
- **Score: X/100**
- **Conformité: X%**
- **Écarts critiques:**
- **Recommandations:**

**FONCTION 5: RECOVER (Récupérer)**
Évalue:
- Plan de continuité (BCP)
- Plan de reprise (DRP)
- Sauvegarde: ${responses.backup_recovery}
- Communications de crise
- **Score: X/100**
- **Conformité: X%**
- **Écarts critiques:**
- **Recommandations:**

### **5. ANALYSE ISO 27001:2022 (Annexe A - Contrôles)**

Évaluer la conformité aux contrôles ISO 27001 par catégorie:

**A.5 Contrôles organisationnels**
- Politiques de sécurité
- Rôles et responsabilités
- Ségrégation des tâches
- **Conformité: X%**

**A.6 Contrôles de personnel**
- Vérification des antécédents
- Formation: ${responses.security_training}
- Processus disciplinaire
- **Conformité: X%**

**A.7 Contrôles physiques**
- Périmètres de sécurité
- Contrôle d'accès physique
- Sécurité des équipements
- **Conformité: X%**

**A.8 Contrôles technologiques**
- Gestion des accès: ${responses.identity_access}
- Cryptographie: ${responses.data_protection}
- Gestion des vulnérabilités: ${responses.vulnerabilities}
- Logging et monitoring
- **Conformité: X%**

### **6. CONFORMITÉ RÉGLEMENTAIRE**

Pour chaque exigence applicable (${Array.isArray(responses.compliance) ? responses.compliance.join(', ') : ''}):
- État de conformité actuel (%)
- Écarts identifiés
- Pénalités potentielles si non-conforme
- Actions correctives requises

### **7. CARTOGRAPHIE DES RISQUES CYBER**

Créer une matrice des risques:

| Risque | Probabilité | Impact | Niveau | Mitigation |
|--------|-------------|--------|--------|------------|
| Ransomware | Élevé | Critique | CRITIQUE | [Actions] |
| Phishing | Élevé | Élevé | ÉLEVÉ | [Actions] |
| ...

### **8. ANALYSE DES VULNÉRABILITÉS**
- Vulnérabilités techniques identifiées
- Gestion des patches: ${responses.vulnerabilities}
- Configuration erronée
- Comptes privilégiés non protégés
- Données sensibles non chiffrées

### **9. PLAN D'ACTION PRIORISÉ**

**🔴 ACTIONS CRITIQUES (0-30 jours):**
1. [Action détaillée avec justification et coût estimé]
2. ...

**🟠 ACTIONS ÉLEVÉES (1-3 mois):**
1. ...

**🟡 ACTIONS MOYENNES (3-6 mois):**
1. ...

**🟢 ACTIONS BASSES (6-12 mois):**
1. ...

### **10. FEUILLE DE ROUTE SÉCURITÉ**
- Phase 1: Remédiation critique (Quick wins)
- Phase 2: Renforcement défensif
- Phase 3: Détection et réponse
- Phase 4: Excellence et certification (ISO 27001?)

### **11. INVESTISSEMENT ESTIMÉ**
- Coûts par phase
- ROI attendu (réduction du risque, évitement de pertes)
- Comparaison coût de prévention vs coût d'un incident

### **12. CONCLUSION**
- Score de sécurité global: X/100
- Niveau de risque: [FAIBLE/MOYEN/ÉLEVÉ/CRITIQUE]
- Prochaines étapes
- Engagement BZ pour accompagnement

---

## 📐 EXIGENCES DE QUALITÉ

1. **Français impeccable et ton sérieux** (audit = document légal potentiel)
2. **Quantifie TOUT** - scores, %, niveaux de risque
3. **Sois alarmiste si nécessaire** - le client doit comprendre les risques RÉELS
4. **Fournis des chiffres** - coûts moyens d'incidents cyber dans le secteur
5. **Minimum 2500 mots**
6. **Actionnable** - chaque recommandation doit être claire et priorisée

---

**GÉNÈRE MAINTENANT L'AUDIT DE SÉCURITÉ COMPLET !** 🛡️`;

  return await callOpenAI(prompt);
}
*/

// 🔌 APPEL API OPENAI via le backend (clé côté serveur uniquement)
const DIAGNOSTIC_SYSTEM =
  'Tu es le MEILLEUR consultant TI et expert cybersécurité au monde chez Bergeron Zaccheo (BZ). Tu génères des documents professionnels ultra-rigoureux alignés avec les standards COBIT, ITIL, NIST et ISO. Ton français est impeccable. Ton ton est professionnel, expert et orienté solutions.';

async function callOpenAI(prompt: string): Promise<string> {
  return chatCompletions({
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: DIAGNOSTIC_SYSTEM },
      { role: 'user', content: prompt },
    ],
    temperature: 0.7,
    max_tokens: 16000,
  });
}

